import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import useAuthenticatedAxios from "../../../auth/AuthenticatedAxios";
import { useToast } from '../../../Context/ToastContext';
import ImavenLoadingScreen from '../../../Components/Helpers/loading-screen';

// ============================================
// SVG ICONS
// ============================================

const Icons = {
  brain: <div style={{ 
    width: 80, 
    height: 80, 
    borderRadius: 16, 
    background: 'linear-gradient(135deg, rgba(10, 14, 26, 0.98) 0%, rgba(14, 18, 32, 0.95) 100%)', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    overflow: 'hidden'
  }}>
    <img src={require('../../../Images/brain.gif')} alt="Brain" style={{ width: 240, height: 240, objectFit: 'contain' }} />
  </div>,
  star: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  check: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  alert: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
  trend: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
  message: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  lightbulb: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="9" y1="18" x2="15" y2="18"/><line x1="10" y1="22" x2="14" y2="22"/><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/></svg>,
  refresh: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>,
  apply: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/><path d="m9 12 2 2 4-4"/></svg>,
  version: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>,
  loader: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/></svg>,
};

// ============================================
// ANIMATIONS
// ============================================

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const ModuleContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(15, 20, 35, 0.98) 0%, rgba(20, 25, 45, 0.95) 100%);
`;

const Header = styled.div`
  padding: 24px 32px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  h2 {
    margin: 0;
    font-size: 22px;
    font-weight: 600;
    background: linear-gradient(135deg, #a5b4fc 0%, #818cf8 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  svg {
    color: #818cf8;
    width: 24px;
    height: 24px;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: rgba(99, 102, 241, 0.15);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 8px;
  color: #a5b4fc;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(99, 102, 241, 0.25);
    border-color: rgba(99, 102, 241, 0.5);
  }
  
  ${props => props.loading && css`
    svg {
      animation: ${spin} 1s linear infinite;
    }
  `}
`;

const ContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px 32px;
  
  /* Hide scrollbar but keep scroll functionality */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 32px;
`;

const StatCard = styled.div`
  background: rgba(25, 30, 50, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 16px;
  padding: 24px;
  animation: ${slideIn} 0.3s ease-out;
  animation-delay: ${props => props.$delay || '0s'};
  animation-fill-mode: both;
`;

const StatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const StatLabel = styled.span`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(165, 180, 252, 0.7);
`;

const StatIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${props => props.$bg || 'rgba(99, 102, 241, 0.2)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$color || '#a5b4fc'};
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 8px;
`;

const StatSubtext = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
`;

// Expert Status Panel
const ExpertStatusSection = styled.div`
  background: rgba(25, 30, 50, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 16px;
  padding: 20px 24px;
  margin-bottom: 24px;
  animation: ${slideIn} 0.3s ease-out;
  overflow: hidden;
`;

const ExpertStatusTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: #a5b4fc;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ExpertStatusGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
`;

const ExpertStatusCard = styled.div`
  background: rgba(15, 20, 35, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.1);
  border-radius: 10px;
  padding: 16px;
`;

const ExpertStatusLabel = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`;

const ExpertStatusValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.$highlight ? '#10b981' : '#f59e0b'};
`;

const ExpertStatusDescription = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
  line-height: 1.5;
  margin: 16px 0 0 0;
`;

const HealthCard = styled(StatCard)`
  grid-column: span 2;
  display: flex;
  gap: 24px;
  align-items: center;
  overflow: visible;
  position: relative;
  animation: none;
`;

const HealthScore = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: conic-gradient(
    ${props => props.$color || '#818cf8'} ${props => (props.$score || 0) * 3.6}deg,
    rgba(255, 255, 255, 0.1) 0deg
  );
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    width: 90px;
    height: 90px;
    background: rgba(25, 30, 50, 1);
    border-radius: 50%;
  }
`;

const HealthScoreValue = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  
  .score {
    font-size: 28px;
    font-weight: 700;
    color: #fff;
    line-height: 1;
  }
  
  .label {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.5);
    text-transform: uppercase;
    margin-top: 2px;
    letter-spacing: 0.5px;
  }
`;

const HealthSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.4);
  font-size: 12px;
  margin: 4px 0 0;
  line-height: 1.4;
`;

const InfoTooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: help;
  
  .info-icon {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.4);
    font-size: 10px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 6px;
    flex-shrink: 0;
    transition: all 0.2s;
  }
  
  .tooltip-content {
    display: none;
    position: fixed;
    background: #0f1428;
    border: 1px solid rgba(147, 51, 234, 0.3);
    border-radius: 10px;
    padding: 12px 16px;
    min-width: 280px;
    z-index: 2147483647;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6);
    pointer-events: none;
  }
  
  &:hover .info-icon {
    background: rgba(147, 51, 234, 0.3);
    color: #a78bfa;
  }
  
  &:hover .tooltip-content {
    display: block;
  }
  
  .tooltip-title {
    font-size: 12px;
    font-weight: 600;
    color: #a78bfa;
    margin-bottom: 8px;
  }
  
  .tooltip-item {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.6);
    line-height: 1.5;
    margin-bottom: 2px;
  }
`;

const InfoTooltip = ({ children }) => {
  const handleMouseEnter = (e) => {
    const icon = e.currentTarget.querySelector('.info-icon');
    const tooltip = e.currentTarget.querySelector('.tooltip-content');
    if (icon && tooltip) {
      const rect = icon.getBoundingClientRect();
      tooltip.style.top = `${rect.bottom + 8}px`;
      tooltip.style.left = `${Math.max(10, rect.left - 130)}px`;
    }
  };

  return (
    <InfoTooltipWrapper onMouseEnter={handleMouseEnter}>
      {children}
    </InfoTooltipWrapper>
  );
};

const HealthDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const HealthLevel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 16px;
  
  ${props => {
    switch(props.$level) {
      case 'excellent': return css`background: rgba(16, 185, 129, 0.2); color: #10b981;`;
      case 'good': return css`background: rgba(59, 130, 246, 0.2); color: #3b82f6;`;
      case 'needs_attention': return css`background: rgba(245, 158, 11, 0.2); color: #f59e0b;`;
      case 'critical': return css`background: rgba(239, 68, 68, 0.2); color: #ef4444;`;
      default: return css`background: rgba(99, 102, 241, 0.2); color: #a5b4fc;`;
    }
  }}
`;

const HealthSummary = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #a5b4fc;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SuggestionsSection = styled.div`
  margin-bottom: 32px;
`;

const SuggestionCard = styled.div`
  background: rgba(25, 30, 50, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 12px;
  animation: ${slideIn} 0.3s ease-out;
  animation-delay: ${props => props.$delay || '0s'};
  animation-fill-mode: both;
  
  &:hover {
    border-color: rgba(99, 102, 241, 0.4);
  }
`;

const SuggestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const SuggestionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  h4 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: #fff;
  }
`;

const PriorityBadge = styled.span`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  
  ${props => {
    switch(props.$priority) {
      case 'high': return css`background: rgba(239, 68, 68, 0.2); color: #ef4444;`;
      case 'medium': return css`background: rgba(245, 158, 11, 0.2); color: #f59e0b;`;
      case 'low': return css`background: rgba(16, 185, 129, 0.2); color: #10b981;`;
      default: return css`background: rgba(99, 102, 241, 0.2); color: #a5b4fc;`;
    }
  }}
`;

const TypeBadge = styled.span`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  background: rgba(99, 102, 241, 0.15);
  color: #a5b4fc;
`;

const SuggestionDescription = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
  line-height: 1.5;
  margin: 0 0 16px 0;
`;

const SuggestionActions = styled.div`
  display: flex;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(99, 102, 241, 0.1);
`;

const ApplyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #6366f1 0%, #818cf8 100%);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const DismissButton = styled.button`
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.8);
  }
`;

// Modal Styles for Suggestion Details
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: linear-gradient(180deg, rgba(30, 35, 55, 0.98) 0%, rgba(20, 25, 40, 0.99) 100%);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #fff;
  font-size: 18px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  
  &:hover {
    color: rgba(255, 255, 255, 0.9);
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const DetailSection = styled.div`
  margin-bottom: 20px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const DetailValue = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  line-height: 1.6;
`;

const EvidenceList = styled.ul`
  margin: 0;
  padding-left: 20px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  
  li {
    margin-bottom: 8px;
    font-style: italic;
  }
`;

const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const ConversationsToRate = styled.div`
  margin-bottom: 32px;
`;

const RateCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(25, 30, 50, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 10px;
  
  &:hover {
    border-color: rgba(99, 102, 241, 0.4);
  }
`;

const RateCardInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const RateCardCategory = styled.span`
  padding: 4px 12px;
  border-radius: 8px;
  font-size: 12px;
  background: rgba(99, 102, 241, 0.15);
  color: #a5b4fc;
`;

const RateCardTime = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
`;

const RatingButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const RatingButton = styled.button`
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  
  ${props => {
    switch(props.$rating) {
      case 'excellent': return css`
        background: rgba(16, 185, 129, 0.15);
        color: #10b981;
        &:hover { background: rgba(16, 185, 129, 0.3); }
      `;
      case 'good': return css`
        background: rgba(59, 130, 246, 0.15);
        color: #3b82f6;
        &:hover { background: rgba(59, 130, 246, 0.3); }
      `;
      case 'needs_improvement': return css`
        background: rgba(245, 158, 11, 0.15);
        color: #f59e0b;
        &:hover { background: rgba(245, 158, 11, 0.3); }
      `;
      case 'poor': return css`
        background: rgba(239, 68, 68, 0.15);
        color: #ef4444;
        &:hover { background: rgba(239, 68, 68, 0.3); }
      `;
      default: return css`
        background: rgba(99, 102, 241, 0.15);
        color: #a5b4fc;
        &:hover { background: rgba(99, 102, 241, 0.3); }
      `;
    }
  }}
`;

const ViewConvoButton = styled.button`
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid rgba(99, 102, 241, 0.3);
  background: rgba(99, 102, 241, 0.1);
  color: #a5b4fc;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    background: rgba(99, 102, 241, 0.25);
    border-color: rgba(99, 102, 241, 0.5);
  }
`;

const ConvPreview = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  font-style: italic;
  margin-top: 6px;
  max-width: 500px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AIReviewButton = styled.button`
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid rgba(168, 85, 247, 0.3);
  background: rgba(168, 85, 247, 0.1);
  color: #c084fc;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: rgba(168, 85, 247, 0.25);
    border-color: rgba(168, 85, 247, 0.5);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const GradeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  ${props => {
    if (props.$score >= 80) return css`background: rgba(16, 185, 129, 0.15); color: #10b981;`;
    if (props.$score >= 60) return css`background: rgba(245, 158, 11, 0.15); color: #f59e0b;`;
    if (props.$score >= 40) return css`background: rgba(249, 115, 22, 0.15); color: #f97316;`;
    return css`background: rgba(239, 68, 68, 0.15); color: #ef4444;`;
  }}

  &:hover { opacity: 0.8; }
`;

const ReviewModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const ReviewModalContent = styled.div`
  background: #1a1a2e;
  border: 1px solid rgba(168, 85, 247, 0.3);
  border-radius: 16px;
  padding: 28px;
  max-width: 640px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  color: #fff;
`;

const ReviewModalTitle = styled.h3`
  margin: 0 0 20px;
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DimensionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 13px;
`;

const DimensionBar = styled.div`
  width: 120px;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
`;

const DimensionFill = styled.div`
  height: 100%;
  border-radius: 3px;
  transition: width 0.5s ease;
  ${props => {
    if (props.$value >= 80) return css`background: #10b981;`;
    if (props.$value >= 60) return css`background: #f59e0b;`;
    if (props.$value >= 40) return css`background: #f97316;`;
    return css`background: #ef4444;`;
  }}
`;

const IssueBadge = styled.span`
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  ${props => {
    switch (props.$severity) {
      case 'critical': return css`background: rgba(239, 68, 68, 0.15); color: #ef4444;`;
      case 'high': return css`background: rgba(249, 115, 22, 0.15); color: #f97316;`;
      case 'medium': return css`background: rgba(245, 158, 11, 0.15); color: #f59e0b;`;
      default: return css`background: rgba(99, 102, 241, 0.15); color: #a5b4fc;`;
    }
  }}
`;

const Spinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(168, 85, 247, 0.3);
  border-top-color: #c084fc;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const VersionsSection = styled.div`
  margin-bottom: 32px;
`;

const VersionTimeline = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding-bottom: 8px;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.3);
    border-radius: 2px;
  }
`;

const VersionCard = styled.div`
  min-width: 160px;
  background: rgba(25, 30, 50, 0.8);
  border: 1px solid ${props => props.active ? 'rgba(99, 102, 241, 0.5)' : 'rgba(99, 102, 241, 0.2)'};
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  
  ${props => props.active && css`
    background: rgba(99, 102, 241, 0.1);
  `}
`;

const VersionNumber = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 8px;
`;

const VersionMetric = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: ${props => {
    if (props.value >= 80) return '#10b981';
    if (props.value >= 60) return '#3b82f6';
    if (props.value >= 40) return '#f59e0b';
    return '#ef4444';
  }};
  margin-bottom: 4px;
`;

const VersionLabel = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
`;

const VersionTooltipContainer = styled.div`
  cursor: help;
  
  .version-tooltip {
    display: none;
    position: fixed;
    background: linear-gradient(135deg, #0f1428 0%, #141b30 100%);
    border: 1px solid rgba(99, 102, 241, 0.3);
    border-radius: 10px;
    padding: 10px 14px;
    min-width: 220px;
    max-width: 260px;
    z-index: 2147483647;
    pointer-events: none;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6), 0 0 12px rgba(99, 102, 241, 0.1);
  }
  
  &:hover .version-tooltip {
    display: block;
  }
  
  .version-tooltip-title {
    font-size: 11px;
    font-weight: 600;
    color: #a5b4fc;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }
  
  .version-tooltip-text {
    font-size: 12px;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.75);
    line-height: 1.5;
  }
`;

const VersionTooltip = ({ children, title, text }) => {
  const handleMouseEnter = (e) => {
    const trigger = e.currentTarget;
    const tooltip = trigger.querySelector('.version-tooltip');
    if (tooltip) {
      const rect = trigger.getBoundingClientRect();
      const tooltipWidth = 240;
      let left = rect.left + rect.width / 2 - tooltipWidth / 2;
      left = Math.max(10, Math.min(left, window.innerWidth - tooltipWidth - 10));
      tooltip.style.top = `${rect.bottom + 8}px`;
      tooltip.style.left = `${left}px`;
    }
  };

  return (
    <VersionTooltipContainer onMouseEnter={handleMouseEnter}>
      {children}
      <div className="version-tooltip">
        <div className="version-tooltip-title">{title}</div>
        <div className="version-tooltip-text">{text}</div>
      </div>
    </VersionTooltipContainer>
  );
};

const VersionActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;
`;

const VersionActionButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid ${props => props.$danger ? 'rgba(239, 68, 68, 0.3)' : 'rgba(99, 102, 241, 0.3)'};
  background: ${props => props.$danger ? 'rgba(239, 68, 68, 0.1)' : 'rgba(99, 102, 241, 0.1)'};
  color: ${props => props.$danger ? '#ef4444' : '#818cf8'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background: ${props => props.$danger ? 'rgba(239, 68, 68, 0.2)' : 'rgba(99, 102, 241, 0.2)'};
    border-color: ${props => props.$danger ? 'rgba(239, 68, 68, 0.5)' : 'rgba(99, 102, 241, 0.5)'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const VersionActiveBadge = styled.div`
  display: inline-block;
  padding: 4px 10px;
  background: rgba(16, 185, 129, 0.2);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 12px;
  color: #10b981;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  margin-top: 10px;
`;

const VersionConfirmModal = styled.div`
  text-align: left;
  
  h4 {
    color: #fff;
    margin: 0 0 16px 0;
    font-size: 18px;
  }
  
  p {
    color: rgba(255, 255, 255, 0.7);
    margin: 0 0 12px 0;
    line-height: 1.5;
  }
  
  ul {
    margin: 16px 0;
    padding-left: 24px;
    color: rgba(255, 255, 255, 0.8);
    
    li {
      margin-bottom: 8px;
    }
  }
`;

const VersionModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

const VersionModalButton = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => props.$primary ? `
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    border: none;
    color: white;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
    }
  ` : `
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.7);
    
    &:hover {
      border-color: rgba(255, 255, 255, 0.4);
      color: white;
    }
  `}
`;

// ============================================
// CHART STYLED COMPONENTS
// ============================================

const AnalyticsSection = styled.div`
  margin-bottom: 32px;
`;

const ChartContainer = styled.div`
  background: rgba(25, 30, 50, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 20px;
`;

const ChartTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #a5b4fc;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BarChartContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  height: 160px;
  padding: 0 8px;
`;

const BarGroup = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const BarWrapper = styled.div`
  display: flex;
  gap: 2px;
  height: 140px;
  align-items: flex-end;
`;

const Bar = styled.div`
  width: 12px;
  height: ${props => Math.max(4, props.height)}px;
  background: ${props => props.color || '#818cf8'};
  border-radius: 4px 4px 0 0;
  transition: height 0.3s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const BarLabel = styled.div`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
  white-space: nowrap;
`;

const ChartLegend = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid rgba(99, 102, 241, 0.1);
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
`;

const LegendDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => props.color};
`;

const TrendList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TrendItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: rgba(15, 20, 35, 0.6);
  border-radius: 10px;
  border: 1px solid rgba(99, 102, 241, 0.1);
`;

const TrendCategory = styled.span`
  font-size: 14px;
  color: #fff;
`;

const TrendIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  
  ${props => {
    switch(props.trend) {
      case 'improving': return css`color: #10b981;`;
      case 'declining': return css`color: #ef4444;`;
      default: return css`color: #a5b4fc;`;
    }
  }}
  
  svg {
    ${props => props.trend === 'declining' && css`transform: rotate(180deg);`}
    ${props => props.trend === 'stable' && css`transform: rotate(90deg);`}
  }
`;

const TrendArrow = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 19V5M5 12l7-7 7 7"/>
  </svg>
);

const TabsContainer = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 20px;
  background: rgba(15, 20, 35, 0.6);
  padding: 4px;
  border-radius: 10px;
  width: fit-content;
`;

const Tab = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => props.active ? css`
    background: rgba(99, 102, 241, 0.3);
    color: #a5b4fc;
  ` : css`
    background: transparent;
    color: rgba(255, 255, 255, 0.5);
    &:hover {
      color: rgba(255, 255, 255, 0.8);
    }
  `}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: rgba(255, 255, 255, 0.5);
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
  }
  
  p {
    margin: 0;
    font-size: 14px;
  }
`;

const LoadingSpinner = styled.div`
  svg {
    animation: ${spin} 1s linear infinite;
    color: #a5b4fc;
  }
`;

// Missing Components Section
const MissingComponentsSection = styled.div`
  margin: 24px 0;
  padding: 20px;
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 12px;
  animation: ${slideIn} 0.3s ease-out;
`;

const MissingSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  
  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #fca5a5;
  }
  
  svg {
    color: #ef4444;
    width: 20px;
    height: 20px;
  }
`;

const MissingComponentCard = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  border-left: 3px solid ${props => props.$severity === 'critical' ? '#ef4444' : props.$severity === 'warning' ? '#f59e0b' : '#3b82f6'};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const MissingComponentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const MissingComponentName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  span.icon {
    font-size: 16px;
  }
  
  span.name {
    font-weight: 600;
    color: #fff;
    font-size: 14px;
  }
`;

const MissingComponentBadge = styled.span`
  font-size: 10px;
  padding: 3px 8px;
  border-radius: 4px;
  text-transform: uppercase;
  font-weight: 600;
  background: ${props => props.$severity === 'critical' ? 'rgba(239, 68, 68, 0.2)' : props.$severity === 'warning' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(59, 130, 246, 0.2)'};
  color: ${props => props.$severity === 'critical' ? '#fca5a5' : props.$severity === 'warning' ? '#fcd34d' : '#93c5fd'};
`;

const MissingComponentDescription = styled.p`
  margin: 0 0 8px 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;
`;

const MissingComponentAction = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: rgba(99, 102, 241, 0.9);
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const HowToFix = styled.div`
  margin-top: 10px;
  padding: 10px 12px;
  background: rgba(99, 102, 241, 0.1);
  border-radius: 6px;
  font-size: 12px;
  color: rgba(165, 180, 252, 0.9);
  line-height: 1.5;
  
  strong {
    color: #a5b4fc;
  }
`;

// ============================================
// NEW DASHBOARD SECTION STYLED COMPONENTS
// ============================================

const NewSection = styled.div`
  margin-bottom: 32px;
  animation: ${slideIn} 0.3s ease-out;
  animation-delay: ${props => props.$delay || '0s'};
  animation-fill-mode: both;
`;

const NewSectionCard = styled.div`
  background: rgba(25, 30, 50, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 16px;
  padding: 24px;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
`;

const MiniStat = styled.div`
  background: rgba(15, 20, 35, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.1);
  border-radius: 10px;
  padding: 14px;
  text-align: center;
`;

const MiniStatValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${props => props.$color || '#fff'};
`;

const MiniStatLabel = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 4px;
`;

const TimelineList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TimelineItem = styled.div`
  position: relative;
  padding: 16px 20px;
  background: rgba(15, 20, 35, 0.6);
  border-radius: 10px;
  border-left: 3px solid ${props => props.$borderColor || 'rgba(99, 102, 241, 0.5)'};
  
  &:hover {
    background: rgba(15, 20, 35, 0.8);
  }
`;

const TimelineItemHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const TimelineItemTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
`;

const TimelineItemMeta = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => props.$bg || 'rgba(99, 102, 241, 0.2)'};
  color: ${props => props.$color || '#a5b4fc'};
`;

const PipelineRow = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding-bottom: 8px;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.3);
    border-radius: 2px;
  }
`;

const PipelineCard = styled.div`
  min-width: 220px;
  background: rgba(15, 20, 35, 0.6);
  border: 1px solid ${props => props.$borderColor || 'rgba(99, 102, 241, 0.2)'};
  border-radius: 12px;
  padding: 18px;
  flex-shrink: 0;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  margin: 10px 0;
  overflow: hidden;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  border-radius: 4px;
  background: ${props => props.$color || '#6366f1'};
  width: ${props => Math.min(100, props.$percent || 0)}%;
  transition: width 0.5s ease;
`;

const StreakDots = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const StreakDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => props.$success ? '#10b981' : '#ef4444'};
`;

const ABSplitContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 0;
  align-items: stretch;
`;

const ABSide = styled.div`
  background: rgba(15, 20, 35, 0.6);
  border: 1px solid ${props => props.$winning ? 'rgba(16, 185, 129, 0.4)' : 'rgba(99, 102, 241, 0.15)'};
  border-radius: ${props => props.$side === 'left' ? '12px 0 0 12px' : '0 12px 12px 0'};
  padding: 24px;
  text-align: center;
`;

const ABDivider = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  background: rgba(15, 20, 35, 0.3);
`;

const ABVsBadge = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6366f1, #818cf8);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
`;

const ABMetric = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${props => props.$color || '#fff'};
  margin-bottom: 4px;
`;

const ABMetricLabel = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 12px;
`;

const ABVersionLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #a5b4fc;
  margin-bottom: 16px;
`;

const ABFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid rgba(99, 102, 241, 0.1);
`;

const ABFormRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
  flex-wrap: wrap;
`;

const ABFormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  
  label {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.5);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  input, select {
    padding: 8px 12px;
    background: rgba(15, 20, 35, 0.8);
    border: 1px solid rgba(99, 102, 241, 0.3);
    border-radius: 8px;
    color: #fff;
    font-size: 14px;
    outline: none;
    
    &:focus {
      border-color: #6366f1;
    }
  }
`;

const StrategyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;

const StrategyCard = styled.div`
  background: rgba(15, 20, 35, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 12px;
  padding: 18px;
  
  &:hover {
    border-color: rgba(99, 102, 241, 0.3);
  }
`;

const StrategyName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 10px;
`;

const StrategyMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
`;

const FunnelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FunnelRow = styled.div`
  background: rgba(15, 20, 35, 0.6);
  border-radius: 10px;
  padding: 16px 20px;
  border: 1px solid rgba(99, 102, 241, 0.1);
`;

const FunnelPathwayName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 12px;
`;

const FunnelBars = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FunnelBarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FunnelBarLabel = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  width: 70px;
  flex-shrink: 0;
  text-transform: uppercase;
`;

const FunnelBarTrack = styled.div`
  flex: 1;
  height: 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  overflow: hidden;
  position: relative;
`;

const FunnelBarFill = styled.div`
  height: 100%;
  border-radius: 4px;
  background: ${props => props.$color || '#6366f1'};
  width: ${props => props.$percent || 0}%;
  transition: width 0.5s ease;
  display: flex;
  align-items: center;
  padding-left: 8px;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  min-width: ${props => props.$percent > 0 ? '30px' : '0'};
`;

const FunnelBarValue = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  width: 40px;
  text-align: right;
  flex-shrink: 0;
`;

const VisitorList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const VisitorRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: rgba(15, 20, 35, 0.6);
  border-radius: 10px;
  border: 1px solid rgba(99, 102, 241, 0.1);
`;

const VisitorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const VisitorAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.3), rgba(129, 140, 248, 0.3));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  color: #a5b4fc;
`;

const VisitorDetails = styled.div``;

const VisitorName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #fff;
`;

const VisitorMeta = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 2px;
`;

const EngagementBar = styled.div`
  width: 80px;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
`;

const EngagementFill = styled.div`
  height: 100%;
  border-radius: 3px;
  width: ${props => props.$score || 0}%;
  background: ${props => {
    const s = props.$score || 0;
    if (s >= 70) return '#10b981';
    if (s >= 40) return '#f59e0b';
    return '#ef4444';
  }};
`;

const CircularProgress = styled.div`
  width: ${props => props.$size || 60}px;
  height: ${props => props.$size || 60}px;
  border-radius: 50%;
  background: conic-gradient(
    ${props => props.$color || '#6366f1'} ${props => (props.$value || 0) * 3.6}deg,
    rgba(255, 255, 255, 0.1) 0deg
  );
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    width: ${props => (props.$size || 60) - 12}px;
    height: ${props => (props.$size || 60) - 12}px;
    background: rgba(15, 20, 35, 0.95);
    border-radius: 50%;
  }
`;

const CircularProgressValue = styled.div`
  position: relative;
  z-index: 1;
  font-size: ${props => props.$fontSize || '14px'};
  font-weight: 700;
  color: #fff;
`;

const DangerButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  color: #ef4444;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(239, 68, 68, 0.25);
    border-color: rgba(239, 68, 68, 0.5);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DurationBadge = styled.span`
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 12px;
  background: rgba(99, 102, 241, 0.15);
  color: #a5b4fc;
`;

const ShowMoreButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 10px;
  margin-top: 12px;
  background: rgba(99, 102, 241, 0.08);
  border: 1px dashed rgba(99, 102, 241, 0.25);
  border-radius: 8px;
  color: #a5b4fc;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(99, 102, 241, 0.15);
    border-color: rgba(99, 102, 241, 0.4);
  }
`;

const ListCount = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.35);
  margin-left: auto;
`;

const DemoBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.25), rgba(251, 191, 36, 0.15));
  border: 1px solid rgba(245, 158, 11, 0.3);
  color: #fbbf24;
  margin-left: 10px;
`;

// ============================================
// MAIN COMPONENT
// ============================================

// ============================================
// DEMO / MOCK DATA (shown until real data arrives)
// ============================================

const MOCK_TEACHINGS = [
  { _id: 'demo-1', name: 'Clarify processing times upfront', status: 'shadow', source: { type: 'system_learned', sentiment: 'negative', fromRating: 'poor' }, createdAt: new Date(Date.now() - 2 * 86400000).toISOString(), shadowStats: { conversationsTested: 3, successRate: 67, promotionThreshold: 5, successStreak: 2, failStreak: 1 }, triggers: ['processing time'] },
  { _id: 'demo-2', name: 'Recommend document checklist early', status: 'shadow', source: { type: 'system_learned', sentiment: 'negative', fromRating: 'needs_improvement' }, createdAt: new Date(Date.now() - 5 * 86400000).toISOString(), shadowStats: { conversationsTested: 4, successRate: 75, promotionThreshold: 5, successStreak: 3, failStreak: 0 }, triggers: ['documents needed'] },
  { _id: 'demo-3', name: 'Reinforce empathetic tone on rejections', status: 'active', source: { type: 'system_learned', sentiment: 'positive', fromRating: 'excellent' }, createdAt: new Date(Date.now() - 10 * 86400000).toISOString(), shadowStats: { conversationsTested: 5, successRate: 80 } },
  { _id: 'demo-4', name: 'Suggest alternative pathways proactively', status: 'active', source: { type: 'system_learned', sentiment: 'positive', fromRating: 'excellent' }, createdAt: new Date(Date.now() - 14 * 86400000).toISOString(), shadowStats: { conversationsTested: 6, successRate: 83 } },
  { _id: 'demo-5', name: 'Avoid technical jargon with first-time visitors', status: 'shadow', source: { type: 'system_learned', sentiment: 'negative', fromRating: 'poor' }, createdAt: new Date(Date.now() - 1 * 86400000).toISOString(), shadowStats: { conversationsTested: 1, successRate: 100, promotionThreshold: 5, successStreak: 1, failStreak: 0 }, triggers: ['confused', 'what does that mean'] },
  { _id: 'demo-6', name: 'Always mention fee ranges when asked', status: 'inactive', source: { type: 'system_learned', sentiment: 'negative', fromRating: 'poor' }, createdAt: new Date(Date.now() - 20 * 86400000).toISOString(), shadowStats: { conversationsTested: 5, successRate: 20 } },
];

const MOCK_STRATEGIES = [
  { _id: 's1', name: 'Francophone Immigration Strategy', triggeredCount: 34, successRate: 82, successCount: 28 },
  { _id: 's2', name: 'Express Entry Optimization', triggeredCount: 28, successRate: 71, successCount: 20 },
  { _id: 's3', name: 'Family Sponsorship Pathway', triggeredCount: 22, successRate: 64, successCount: 14 },
  { _id: 's4', name: 'Student to PR Transition', triggeredCount: 19, successRate: 89, successCount: 17 },
  { _id: 's5', name: 'LMIA Exemption Discovery', triggeredCount: 15, successRate: 47, successCount: 7 },
  { _id: 's6', name: 'Provincial Nominee Matching', triggeredCount: 12, successRate: 75, successCount: 9 },
  { _id: 's7', name: 'Refugee Claim Assessment', triggeredCount: 5, successRate: 40, successCount: 2 },
  { _id: 's8', name: 'Business Immigration Qualifier', triggeredCount: 0, successRate: 0, successCount: 0 },
];

const MOCK_FUNNEL = {
  success: true,
  overview: { totalConversations: 186, conversationsWithPathways: 124, pathwayDetectionRate: 67, uniquePathways: 5 },
  funnel: [
    { pathwayId: 'work-permit', pathwayName: 'Work Permit', detected: 52, engaged: 38, converted: 24, failed: 8, unrated: 6, conversionRate: 75, avgMessages: 8.3 },
    { pathwayId: 'express-entry', pathwayName: 'Express Entry', detected: 34, engaged: 28, converted: 18, failed: 5, unrated: 5, conversionRate: 78, avgMessages: 11.2 },
    { pathwayId: 'study-permit', pathwayName: 'Study Permit', detected: 22, engaged: 15, converted: 9, failed: 4, unrated: 2, conversionRate: 69, avgMessages: 6.7 },
    { pathwayId: 'family-sponsor', pathwayName: 'Family Sponsorship', detected: 12, engaged: 8, converted: 4, failed: 3, unrated: 1, conversionRate: 57, avgMessages: 14.1 },
    { pathwayId: 'pnp', pathwayName: 'Provincial Nominee', detected: 4, engaged: 3, converted: 1, failed: 1, unrated: 1, conversionRate: 50, avgMessages: 9.5 },
  ]
};

const MOCK_VISITORS = {
  success: true,
  totalVisitors: 47,
  returningVisitors: 12,
  avgEngagementScore: 62,
  topCategories: [
    { category: 'Work Permits', count: 38 },
    { category: 'Express Entry', count: 24 },
    { category: 'Study Permits', count: 15 },
  ],
  recentReturning: [
    { visitorId: 'v1', displayName: 'Maria S.', totalConversations: 5, lastSeen: new Date(Date.now() - 1 * 86400000).toISOString(), topInterests: ['Work Permit', 'LMIA'], engagementScore: 88 },
    { visitorId: 'v2', displayName: null, totalConversations: 3, lastSeen: new Date(Date.now() - 2 * 86400000).toISOString(), topInterests: ['Express Entry', 'CRS Score'], engagementScore: 72 },
    { visitorId: 'v3', displayName: 'David K.', totalConversations: 4, lastSeen: new Date(Date.now() - 3 * 86400000).toISOString(), topInterests: ['Study Permit'], engagementScore: 65 },
    { visitorId: 'v4', displayName: 'Priya M.', totalConversations: 2, lastSeen: new Date(Date.now() - 5 * 86400000).toISOString(), topInterests: ['Family Sponsorship', 'PR Card'], engagementScore: 51 },
    { visitorId: 'v5', displayName: null, totalConversations: 2, lastSeen: new Date(Date.now() - 7 * 86400000).toISOString(), topInterests: ['PNP', 'Ontario'], engagementScore: 43 },
  ]
};

const MOCK_AB_TEST = {
  isActive: true,
  versionA: 15,
  versionB: 17,
  trafficSplit: 50,
  startedAt: new Date(Date.now() - 4 * 86400000).toISOString(),
  versionAStats: { conversations: 23, positiveRatings: 16, negativeRatings: 5 },
  versionBStats: { conversations: 21, positiveRatings: 14, negativeRatings: 4 },
};

// How many unrated conversations the rating section offers, newest first.
// Rating older cases has diminishing value — the brain learns from recent behaviour.
const TO_RATE_PAGE_SIZE = 20;

const BrainLearningModule = () => {
  const axios = useAuthenticatedAxios();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [conversationsToRate, setConversationsToRate] = useState([]);
  const [totalToRate, setTotalToRate] = useState(0); // full backlog, may exceed the 20 shown
  const [aiReviewLoading, setAiReviewLoading] = useState({});
  const [aiReviewResults, setAiReviewResults] = useState({});
  const [selectedReview, setSelectedReview] = useState(null);
  const [applyingId, setApplyingId] = useState(null);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsTab, setAnalyticsTab] = useState('daily'); // 'daily' | 'trends'
  const [selectedSuggestion, setSelectedSuggestion] = useState(null); // For detail modal
  const [creatingVersion, setCreatingVersion] = useState(false); // For manual version creation
  const [versionModal, setVersionModal] = useState(null); // 'create' | 'manage' | null
  const [rollingBackVersion, setRollingBackVersion] = useState(null); // Version number being rolled back to
  const [deletingVersion, setDeletingVersion] = useState(null); // Version number being deleted
  const [expertStatus, setExpertStatus] = useState(null);
  const [teachings, setTeachings] = useState([]);
  const [abTestData, setAbTestData] = useState(null);
  const [strategies, setStrategies] = useState([]);
  const [funnelData, setFunnelData] = useState(null);
  const [visitorInsights, setVisitorInsights] = useState(null);
  const [trainingPriorities, setTrainingPriorities] = useState([]);
  const [patternSuggestions, setPatternSuggestions] = useState([]);
  const [abTestForm, setAbTestForm] = useState({ versionA: '', versionB: '', trafficSplit: 50 });
  const [startingAbTest, setStartingAbTest] = useState(false);
  const [conversationToRemove, setConversationToRemove] = useState(null);
  const [stoppingAbTest, setStoppingAbTest] = useState(false);
  const [autoLearnLimit, setAutoLearnLimit] = useState(5);
  const [strategyLimit, setStrategyLimit] = useState(6);
  const [funnelLimit, setFunnelLimit] = useState(4);
  const [visitorLimit, setVisitorLimit] = useState(5);
  const [pipelineLimit, setPipelineLimit] = useState(5);
  const [convoRateLimit, setConvoRateLimit] = useState(5);

  const hasRealAutoLearnData = Array.isArray(teachings) && teachings.some(t => t.source?.type === 'system_learned' || t.tags?.includes?.('auto-learned'));
  const hasRealStrategyData = Array.isArray(strategies) && strategies.length > 0;
  const hasRealFunnelData = funnelData && funnelData.funnel && funnelData.funnel.length > 0;
  const hasRealVisitorData = visitorInsights && visitorInsights.totalVisitors > 0;
  const hasRealAbTestData = abTestData && abTestData.isActive;

  const displayTeachings = hasRealAutoLearnData ? teachings : MOCK_TEACHINGS;
  const displayStrategies = hasRealStrategyData ? strategies : MOCK_STRATEGIES;
  const displayFunnel = hasRealFunnelData ? funnelData : MOCK_FUNNEL;
  const displayVisitors = hasRealVisitorData ? visitorInsights : MOCK_VISITORS;
  const displayAbTest = hasRealAbTestData ? abTestData : MOCK_AB_TEST;

  /**
   * Loaded in tiers rather than one Promise.all.
   *
   * Every one of these eleven calls used to be awaited together, so the page
   * stayed on the spinner until the SLOWEST finished — the whole tab was gated
   * by its worst endpoint. Now the two calls the header needs resolve first and
   * the page renders; everything else fills in as it arrives, and each section
   * already has its own empty/loading state.
   */
  const fetchDashboard = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);

    // Tier 1 — what the page cannot render without. Rule-based suggestions only;
    // the AI pass is an LLM round-trip and is merged in by tier 3.
    try {
      const [dashboardRes, toRateRes] = await Promise.all([
        axios.get('/api/brain/my-brain/dashboard'),
        // Only the 20 most recent unrated conversations are worth rating — the
        // endpoint sorts by startTime desc, so this is the newest 20. The full
        // backlog is still reported by the API's `total` (see totalToRate).
        axios.get(`/api/brain/conversations/to-rate?limit=${TO_RATE_PAGE_SIZE}`)
      ]);

      setDashboard(dashboardRes.data);
      setConversationsToRate(toRateRes.data.conversations || []);
      setTotalToRate(toRateRes.data.total || 0);
      setError(null);
    } catch (err) {
      console.error('Error fetching brain dashboard:', err);
      setError('Failed to load brain learning dashboard');
      setLoading(false);
      setRefreshing(false);
      return;
    }

    // Page is usable from here — the rest arrives behind it.
    setLoading(false);
    setRefreshing(false);

    // Tier 2 — panels further down. Each settles independently, so one slow or
    // failing endpoint no longer holds up the others.
    const settle = (promise, apply) =>
      promise.then(res => apply(res)).catch(err => {
        console.warn('[BrainLearning] secondary fetch failed:', err?.message || err);
      });

    settle(axios.get('/api/brain/my-brain/analytics/history?days=14'),
      res => setAnalytics(res.data));
    settle(axios.get('/context-manager/expert-status'),
      res => setExpertStatus(res.data));
    settle(axios.get('/api/brain/v2/teachings'),
      res => setTeachings(res.data?.teachings || res.data?.data || res.data || []));
    settle(axios.get('/api/brain/ab-test/results'),
      res => setAbTestData(res.data?.abTest || null));
    settle(axios.get('/api/expert-strategies'),
      res => setStrategies(res.data?.strategies || res.data || []));
    settle(axios.get('/api/brain/analytics/pathway-funnel?days=30'),
      res => setFunnelData(res.data));
    settle(axios.get('/api/brain/analytics/visitor-insights'),
      res => setVisitorInsights(res.data));
    settle(axios.get('/api/brain/training-priorities'),
      res => setTrainingPriorities(res.data?.priorities || []));
    settle(axios.get('/api/brain/pattern-suggestions'),
      res => setPatternSuggestions(res.data?.suggestions || []));

    // Tier 3 — the AI suggestion pass. Slowest of the lot, and purely additive:
    // it replaces the rule-based suggestion set once the model responds.
    settle(axios.get('/api/brain/my-brain/dashboard?includeAi=true'),
      res => setDashboard(prev => ({ ...(prev || {}), ...res.data })));
  }, [axios]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Rate a conversation with optional outcome
  const [outcomeSelections, setOutcomeSelections] = useState({});

  const dismissPattern = async (id) => {
    try {
      await axios.post(`/api/brain/pattern-suggestions/${id}/dismiss`);
      setPatternSuggestions(prev => prev.filter(s => s._id !== id));
    } catch (err) {
      console.error('Error dismissing pattern:', err);
    }
  };

  const createTeachingFromPattern = async (id) => {
    try {
      await axios.post(`/api/brain/pattern-suggestions/${id}/create-teaching`);
      setPatternSuggestions(prev => prev.filter(s => s._id !== id));
      fetchDashboard(true);
    } catch (err) {
      console.error('Error creating teaching from pattern:', err);
    }
  };
  
  const rateConversation = async (conversationId, rating) => {
    try {
      const outcome = outcomeSelections[conversationId] || null;
      await axios.post(`/api/brain/conversations/${conversationId}/rate`, { 
        rating,
        outcome: outcome || undefined
      });
      setConversationsToRate(prev => prev.filter(c => c.id !== conversationId));
      setOutcomeSelections(prev => { const n = {...prev}; delete n[conversationId]; return n; });
      fetchDashboard(true);
    } catch (err) {
      console.error('Error rating conversation:', err);
    }
  };

  const removeConversationFromRating = async (conversationId) => {
    try {
      await axios.post(`/api/brain/conversations/${conversationId}/remove-from-rating`, {
        reason: 'Removed from rating queue by expert'
      });
      setConversationsToRate(prev => prev.filter(c => c.id !== conversationId));
      setOutcomeSelections(prev => {
        const next = { ...prev };
        delete next[conversationId];
        return next;
      });
      setConversationToRemove(null);
      showToast('Conversation removed from rating list', 'success');
      fetchDashboard(true);
    } catch (err) {
      console.error('Error removing conversation from rating list:', err);
      showToast('Failed to remove conversation from rating list', 'error');
    }
  };

  const triggerAIReview = async (conversationId) => {
    setAiReviewLoading(prev => ({ ...prev, [conversationId]: true }));
    try {
      const { data } = await axios.post(`/api/brain/conversations/${conversationId}/ai-review`);
      if (data.success) {
        setAiReviewResults(prev => ({ ...prev, [conversationId]: data.review }));
        showToast(`AI Review complete: score ${data.review.score}/100`, 'success');
      } else {
        showToast(data.error || 'AI review failed', 'error');
      }
    } catch (err) {
      console.error('Error triggering AI review:', err);
      showToast('Failed to run AI review', 'error');
    } finally {
      setAiReviewLoading(prev => ({ ...prev, [conversationId]: false }));
    }
  };

  // Apply a suggestion
  const applySuggestion = async (suggestion) => {
    setApplyingId(suggestion.id);
    try {
      await axios.post(`/api/brain/my-brain/suggestions/${suggestion.id}/apply`, { suggestion });
      // Refresh dashboard
      fetchDashboard(true);
    } catch (err) {
      console.error('Error applying suggestion:', err);
    } finally {
      setApplyingId(null);
    }
  };

  // Show confirmation modal before creating version
  const handleCreateVersionClick = () => {
    setVersionModal('create');
  };

  // Create a new brain version manually (after confirmation)
  const createNewVersion = async () => {
    setCreatingVersion(true);
    setVersionModal(null);
    try {
      await axios.post('/api/brain/my-brain/version', { 
        note: 'Manual version checkpoint' 
      });
      // Refresh dashboard to show new version
      fetchDashboard(true);
    } catch (err) {
      console.error('Error creating brain version:', err);
    } finally {
      setCreatingVersion(false);
    }
  };

  // Rollback to a specific version
  const rollbackToVersion = async (version) => {
    setRollingBackVersion(version);
    try {
      await axios.post(`/api/brain/my-brain/versions/${version}/activate`);
      // Refresh dashboard
      fetchDashboard(true);
    } catch (err) {
      console.error('Error rolling back to version:', err);
    } finally {
      setRollingBackVersion(null);
    }
  };

  // Delete a specific version
  const deleteVersion = async (version) => {
    if (!window.confirm(`Are you sure you want to delete version ${version}? This cannot be undone.`)) {
      return;
    }
    setDeletingVersion(version);
    try {
      await axios.delete(`/api/brain/my-brain/versions/${version}`);
      // Refresh dashboard
      fetchDashboard(true);
    } catch (err) {
      console.error('Error deleting version:', err);
      alert(err.response?.data?.error || 'Failed to delete version');
    } finally {
      setDeletingVersion(null);
    }
  };

  const startAbTest = async () => {
    if (!abTestForm.versionA || !abTestForm.versionB) return;
    setStartingAbTest(true);
    try {
      await axios.post('/api/brain/ab-test/start', {
        versionA: Number(abTestForm.versionA),
        versionB: Number(abTestForm.versionB),
        trafficSplit: abTestForm.trafficSplit
      });
      fetchDashboard(true);
    } catch (err) {
      console.error('Error starting A/B test:', err);
    } finally {
      setStartingAbTest(false);
    }
  };

  const stopAbTest = async () => {
    setStoppingAbTest(true);
    try {
      await axios.post('/api/brain/ab-test/stop');
      fetchDashboard(true);
    } catch (err) {
      console.error('Error stopping A/B test:', err);
    } finally {
      setStoppingAbTest(false);
    }
  };

  const getHealthColor = (score) => {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#3b82f6';
    if (score >= 40) return '#f59e0b';
    return '#ef4444';
  };

  if (loading) {
    return <ImavenLoadingScreen />;
  }

  if (error) {
    return (
      <ModuleContainer>
        <EmptyState>
          {Icons.alert}
          <p>{error}</p>
          <RefreshButton onClick={() => fetchDashboard()} style={{ marginTop: 16 }}>
            {Icons.refresh} Retry
          </RefreshButton>
        </EmptyState>
      </ModuleContainer>
    );
  }

  return (
    <ModuleContainer>
      <Header>
        <HeaderTitle>
          {Icons.brain}
          <h2>Brain Learning Dashboard</h2>
        </HeaderTitle>
        <HeaderActions>
          <RefreshButton onClick={() => fetchDashboard(true)} loading={refreshing}>
            {Icons.refresh}
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </RefreshButton>
        </HeaderActions>
      </Header>
      
      <ContentArea>
        {/* Health & Stats Overview */}
        <DashboardGrid>
          <HealthCard $delay="0s">
            <HealthScore 
              $score={dashboard?.health?.score || 0}
              $color={getHealthColor(dashboard?.health?.score || 0)}
            >
              <HealthScoreValue>
                <div className="score">{dashboard?.health?.score || 0}</div>
                <div className="label">Performance</div>
              </HealthScoreValue>
            </HealthScore>
            <HealthDetails>
              <HealthLevel $level={dashboard?.health?.level}>
                {Icons.check}
                {dashboard?.health?.level?.replace('_', ' ') || 'Unknown'}
                <InfoTooltip>
                  <span className="info-icon">?</span>
                  <div className="tooltip-content">
                    <div className="tooltip-title">Brain Performance Score</div>
                    <div className="tooltip-item">Measures how well your AI performs in real conversations based on:</div>
                    <div className="tooltip-item">• Conversation success rate & ratings</div>
                    <div className="tooltip-item">• Teaching effectiveness</div>
                    <div className="tooltip-item">• Strategy triggers & results</div>
                    <div className="tooltip-item">• Training session quality</div>
                    <div className="tooltip-item">• Category coverage</div>
                  </div>
                </InfoTooltip>
              </HealthLevel>
              <HealthSummary>
                {dashboard?.health?.summary || 'Loading brain health analysis...'}
              </HealthSummary>
              <HealthSubtitle>Based on real conversation outcomes and AI effectiveness</HealthSubtitle>
            </HealthDetails>
          </HealthCard>
          
          <StatCard $delay="0.1s">
            <StatHeader>
              <StatLabel>Brain Version</StatLabel>
              <StatIcon 
                $bg="rgba(99, 102, 241, 0.2)" 
                $color="#818cf8"
                as="button"
                onClick={handleCreateVersionClick}
                disabled={creatingVersion}
                title="Create new version checkpoint"
                style={{ cursor: 'pointer', border: 'none' }}
              >
                {creatingVersion ? Icons.loader : Icons.version}
              </StatIcon>
            </StatHeader>
            <StatValue>v{dashboard?.brain?.currentVersion || 1}</StatValue>
            <StatSubtext>
              {dashboard?.brain?.flowCount || 0} flows, {dashboard?.brain?.ruleCount || 0} rules
            </StatSubtext>
          </StatCard>
        </DashboardGrid>
        
        <DashboardGrid>
          <StatCard $delay="0.15s">
            <StatHeader>
              <StatLabel>Conversations</StatLabel>
              <StatIcon $bg="rgba(59, 130, 246, 0.2)" $color="#3b82f6">
                {Icons.message}
              </StatIcon>
            </StatHeader>
            <StatValue>{dashboard?.conversations?.total || 0}</StatValue>
            <StatSubtext>{dashboard?.conversations?.rated || 0} rated</StatSubtext>
          </StatCard>
          
          <StatCard $delay="0.2s">
            <StatHeader>
              <StatLabel>To Rate</StatLabel>
              <StatIcon $bg="rgba(245, 158, 11, 0.2)" $color="#f59e0b">
                {Icons.star}
              </StatIcon>
            </StatHeader>
            <StatValue>{dashboard?.conversations?.toRate || 0}</StatValue>
            <StatSubtext>awaiting feedback</StatSubtext>
          </StatCard>
          
          <StatCard $delay="0.25s">
            <StatHeader>
              <StatLabel>Suggestions</StatLabel>
              <StatIcon $bg="rgba(129, 140, 248, 0.2)" $color="#a5b4fc">
                {Icons.lightbulb}
              </StatIcon>
            </StatHeader>
            <StatValue>{dashboard?.suggestions?.count || 0}</StatValue>
            <StatSubtext>
              {dashboard?.suggestions?.highPriority || 0} high priority
            </StatSubtext>
          </StatCard>
        </DashboardGrid>
        
        {/* Expert Status & Data Overview */}
        {(expertStatus || dashboard?.brain) && (
          <ExpertStatusSection>
            <ExpertStatusTitle>
              {Icons.star} Synthora Data Overview
            </ExpertStatusTitle>
            <ExpertStatusGrid>
              {expertStatus && (
                <ExpertStatusCard>
                  <ExpertStatusLabel>Expert Category</ExpertStatusLabel>
                  <ExpertStatusValue $highlight={expertStatus.isImmigration}>
                    {expertStatus.userCategories}
                  </ExpertStatusValue>
                </ExpertStatusCard>
              )}
              <ExpertStatusCard>
                <ExpertStatusLabel>Knowledge Categories</ExpertStatusLabel>
                <ExpertStatusValue $highlight>{expertStatus?.dataCounts?.knowledgeCategories || 0}</ExpertStatusValue>
              </ExpertStatusCard>
              <ExpertStatusCard>
                <ExpertStatusLabel>Rule Sets</ExpertStatusLabel>
                <ExpertStatusValue $highlight>{expertStatus?.dataCounts?.ruleSets || dashboard?.brain?.ruleCount || 0}</ExpertStatusValue>
              </ExpertStatusCard>
              <ExpertStatusCard>
                <ExpertStatusLabel>Scenario Flows</ExpertStatusLabel>
                <ExpertStatusValue $highlight>{expertStatus?.dataCounts?.scenarioFlows || dashboard?.brain?.flowCount || 0}</ExpertStatusValue>
              </ExpertStatusCard>
              <ExpertStatusCard>
                <ExpertStatusLabel>Expert Strategies</ExpertStatusLabel>
                <ExpertStatusValue $highlight>{dashboard?.brain?.expertStrategiesCount || 0}</ExpertStatusValue>
              </ExpertStatusCard>
              <ExpertStatusCard>
                <ExpertStatusLabel>Opportunity Maps</ExpertStatusLabel>
                <ExpertStatusValue $highlight>{dashboard?.brain?.opportunityMapsCount || 0}</ExpertStatusValue>
              </ExpertStatusCard>
              <ExpertStatusCard>
                <ExpertStatusLabel>Teachings</ExpertStatusLabel>
                <ExpertStatusValue $highlight>{dashboard?.brain?.teachingsCount || 0}</ExpertStatusValue>
              </ExpertStatusCard>
              <ExpertStatusCard>
                <ExpertStatusLabel>Training Sessions</ExpertStatusLabel>
                <ExpertStatusValue $highlight>{dashboard?.brain?.trainingSessions || 0}</ExpertStatusValue>
              </ExpertStatusCard>
            </ExpertStatusGrid>
            {expertStatus && (
              <ExpertStatusDescription>
                {expertStatus.isImmigration 
                  ? 'You are an immigration expert. Default categories and content are tailored for immigration services.'
                  : 'You are a non-immigration expert. General categories are loaded by default.'}
              </ExpertStatusDescription>
            )}
          </ExpertStatusSection>
        )}
        
        {/* Missing Components for Perfect Brain - Only show if there are actually missing components */}
        {(
          (!dashboard?.brain?.opportunityMapsCount || dashboard?.brain?.opportunityMapsCount === 0) ||
          (!dashboard?.brain?.teachingsCount || dashboard?.brain?.teachingsCount === 0) ||
          (!dashboard?.brain?.trainingSessions || dashboard?.brain?.trainingSessions === 0) ||
          (!dashboard?.brain?.expertStrategiesCount || dashboard?.brain?.expertStrategiesCount === 0) ||
          (dashboard?.brain?.flowCount === 0 || !dashboard?.brain?.flowCount)
        ) && (
          <MissingComponentsSection>
            <MissingSectionHeader>
              {Icons.alert}
              <h3>Components Missing for Perfect Brain</h3>
            </MissingSectionHeader>
            
            {(!dashboard?.brain?.opportunityMapsCount || dashboard?.brain?.opportunityMapsCount === 0) && (
              <MissingComponentCard $severity="critical">
                <MissingComponentHeader>
                  <MissingComponentName>
                    <span className="icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg></span>
                    <span className="name">Opportunity Maps</span>
                  </MissingComponentName>
                  <MissingComponentBadge $severity="critical">Critical</MissingComponentBadge>
                </MissingComponentHeader>
                <MissingComponentDescription>
                  Opportunity Maps define all possible pathways with requirements, blockers, unlock factors, and alternatives. 
                  Without them, the AI cannot discover hidden opportunities or suggest alternative paths when the primary path fails.
                </MissingComponentDescription>
                <HowToFix>
                  <strong>How to create:</strong> For each of your knowledge categories (e.g., Work Permit, Study Permit), 
                  create an Opportunity Map with pathways. Each pathway should have eligibility requirements, 
                  questions that unlock new possibilities, and alternative paths to suggest.
                </HowToFix>
              </MissingComponentCard>
            )}
            
            {(!dashboard?.brain?.teachingsCount || dashboard?.brain?.teachingsCount === 0) && (
              <MissingComponentCard $severity="warning">
                <MissingComponentHeader>
                  <MissingComponentName>
                    <span className="icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg></span>
                    <span className="name">Teachings</span>
                  </MissingComponentName>
                  <MissingComponentBadge $severity="warning">Recommended</MissingComponentBadge>
                </MissingComponentHeader>
                <MissingComponentDescription>
                  Teachings are corrections the AI has learned from you. When you provide feedback in Training Mode, 
                  those corrections become Teachings that the AI applies in future conversations.
                </MissingComponentDescription>
                <HowToFix>
                  <strong>How to create:</strong> Go to <strong>Training Mode</strong>, simulate conversations with your AI, 
                  rate its responses, and provide corrections. Each correction becomes a Teaching that improves future responses.
                </HowToFix>
              </MissingComponentCard>
            )}
            
            {(!dashboard?.brain?.trainingSessions || dashboard?.brain?.trainingSessions === 0) && (
              <MissingComponentCard $severity="info">
                <MissingComponentHeader>
                  <MissingComponentName>
                    <span className="icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg></span>
                    <span className="name">Training Sessions</span>
                  </MissingComponentName>
                  <MissingComponentBadge $severity="info">Recommended</MissingComponentBadge>
                </MissingComponentHeader>
                <MissingComponentDescription>
                  Training Sessions let you interactively teach your AI by simulating conversations and evaluating responses. 
                  This is the fastest way to improve your AI's performance on specific scenarios.
                </MissingComponentDescription>
                <HowToFix>
                  <strong>How to start:</strong> Go to <strong>Training Mode</strong> in the sidebar, 
                  click "New Training Session", give it a name, and start simulating user conversations.
                </HowToFix>
              </MissingComponentCard>
            )}
            
            {(!dashboard?.brain?.expertStrategiesCount || dashboard?.brain?.expertStrategiesCount === 0) && (
              <MissingComponentCard $severity="warning">
                <MissingComponentHeader>
                  <MissingComponentName>
                    <span className="icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg></span>
                    <span className="name">Expert Strategies</span>
                  </MissingComponentName>
                  <MissingComponentBadge $severity="warning">Recommended</MissingComponentBadge>
                </MissingComponentHeader>
                <MissingComponentDescription>
                  Expert Strategies capture the "what would a good lawyer/expert do" knowledge - the creative workarounds 
                  and strategic solutions that differentiate a skilled professional from basic AI.
                </MissingComponentDescription>
                <HowToFix>
                  <strong>How to create:</strong> Go to <strong>Expert Strategies</strong> in the sidebar, 
                  click "New Strategy", define triggers (keywords that activate it), discovery questions to ask, 
                  and most importantly - the <strong>Expert Insight</strong> (the secret knowledge that makes the strategy valuable).
                </HowToFix>
              </MissingComponentCard>
            )}
            
            {(dashboard?.brain?.flowCount === 0 || !dashboard?.brain?.flowCount) && (
              <MissingComponentCard $severity="warning">
                <MissingComponentHeader>
                  <MissingComponentName>
                    <span className="icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/><line x1="4" y1="4" x2="9" y2="9"/></svg></span>
                    <span className="name">Scenario Flows</span>
                  </MissingComponentName>
                  <MissingComponentBadge $severity="warning">Recommended</MissingComponentBadge>
                </MissingComponentHeader>
                <MissingComponentDescription>
                  Scenario Flows are visual decision trees that guide the AI through complex conversations. 
                  They help the AI ask the right questions in the right order.
                </MissingComponentDescription>
                <HowToFix>
                  <strong>How to create:</strong> Go to <strong>Scenario Flows</strong> in the sidebar, 
                  click "Create New Flow", and build a visual flowchart for your common scenarios.
                </HowToFix>
              </MissingComponentCard>
            )}
          </MissingComponentsSection>
        )}
        
        {/* Version Trend */}
        {dashboard?.versionTrend?.length > 0 && (
          <VersionsSection>
            <SectionTitle>
              {Icons.trend} Version Performance
            </SectionTitle>
            <VersionTimeline>
              {[...dashboard.versionTrend].sort((a, b) => b.isActive ? 1 : a.isActive ? -1 : b.version - a.version).map((v) => (
                <VersionCard key={v.version} active={v.isActive}>
                  <VersionNumber>v{v.version}</VersionNumber>
                  <VersionTooltip
                    title="Success Rate"
                    text={v.conversations > 0 
                      ? `${v.successRate}% of ${v.conversations} rated conversation${v.conversations !== 1 ? 's' : ''} were positive (excellent or good).`
                      : 'No conversations rated yet. Rate conversations below to track this version\'s performance.'}
                  >
                    <VersionMetric value={v.successRate}>
                      {v.successRate}%
                    </VersionMetric>
                  </VersionTooltip>
                  <VersionTooltip
                    title="Rated Conversations"
                    text={`${v.conversations} conversation${v.conversations !== 1 ? 's' : ''} rated on v${v.version}. Rate more to get accurate performance insights.`}
                  >
                    <VersionLabel>
                      {v.conversations} rated
                    </VersionLabel>
                  </VersionTooltip>
                  {!v.isActive && (
                    <VersionActions>
                      <VersionActionButton
                        onClick={() => rollbackToVersion(v.version)}
                        disabled={rollingBackVersion === v.version}
                        title="Rollback to this version"
                      >
                        {rollingBackVersion === v.version ? Icons.loader : '↩'}
                      </VersionActionButton>
                      <VersionActionButton
                        onClick={() => deleteVersion(v.version)}
                        disabled={deletingVersion === v.version}
                        $danger
                        title="Delete this version"
                      >
                        {deletingVersion === v.version ? Icons.loader : '×'}
                      </VersionActionButton>
                    </VersionActions>
                  )}
                  {v.isActive && <VersionActiveBadge>Active</VersionActiveBadge>}
                </VersionCard>
              ))}
            </VersionTimeline>
          </VersionsSection>
        )}
        
        {/* Analytics Charts */}
        {(analytics?.dailyStats?.length > 0 || analytics?.categoryTrend?.length > 0) && (
          <AnalyticsSection>
            <SectionTitle>
              {Icons.trend} Performance Analytics
            </SectionTitle>
            
            <TabsContainer>
              <Tab 
                active={analyticsTab === 'daily'}
                onClick={() => setAnalyticsTab('daily')}
              >
                Daily Stats
              </Tab>
              <Tab 
                active={analyticsTab === 'trends'}
                onClick={() => setAnalyticsTab('trends')}
              >
                Category Trends
              </Tab>
            </TabsContainer>
            
            {analyticsTab === 'daily' && analytics?.dailyStats?.length > 0 && (
              <ChartContainer>
                <ChartTitle>
                  {Icons.message} Daily Conversation Performance (Last 14 Days)
                </ChartTitle>
                <BarChartContainer>
                  {analytics.dailyStats.slice(-14).map((day, idx) => {
                    const maxConvos = Math.max(...analytics.dailyStats.map(d => d.conversations), 1);
                    const positiveHeight = (day.positive / maxConvos) * 120;
                    const negativeHeight = (day.negative / maxConvos) * 120;
                    
                    return (
                      <BarGroup key={day.date}>
                        <BarWrapper>
                          <Bar 
                            height={positiveHeight} 
                            color="#10b981"
                            title={`${day.positive} positive`}
                          />
                          <Bar 
                            height={negativeHeight} 
                            color="#ef4444"
                            title={`${day.negative} negative`}
                          />
                        </BarWrapper>
                        <BarLabel>
                          {new Date(day.date).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                        </BarLabel>
                      </BarGroup>
                    );
                  })}
                </BarChartContainer>
                <ChartLegend>
                  <LegendItem>
                    <LegendDot color="#10b981" />
                    Positive (Excellent/Good)
                  </LegendItem>
                  <LegendItem>
                    <LegendDot color="#ef4444" />
                    Negative (Needs Improvement/Poor)
                  </LegendItem>
                </ChartLegend>
              </ChartContainer>
            )}
            
            {analyticsTab === 'trends' && analytics?.categoryTrend?.length > 0 && (
              <ChartContainer>
                <ChartTitle>
                  {Icons.trend} Category Performance Trends
                </ChartTitle>
                <TrendList>
                  {analytics.categoryTrend.map((cat, idx) => (
                    <TrendItem key={cat.category || idx}>
                      <TrendCategory>{cat.category || 'Unknown'}</TrendCategory>
                      <TrendIndicator trend={cat.trend}>
                        <TrendArrow />
                        {cat.trend === 'improving' && `+${Math.abs(cat.successRateChange)}%`}
                        {cat.trend === 'declining' && `-${Math.abs(cat.successRateChange)}%`}
                        {cat.trend === 'stable' && 'Stable'}
                      </TrendIndicator>
                    </TrendItem>
                  ))}
                </TrendList>
                <div style={{ marginTop: 16, fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
                  Comparing last 2 weeks vs previous 2 weeks
                </div>
              </ChartContainer>
            )}
            
            {analytics?.dailyStats?.length === 0 && analytics?.categoryTrend?.length === 0 && (
              <ChartContainer>
                <EmptyState style={{ padding: '24px' }}>
                  <p>Not enough rated conversations for analytics.</p>
                  <p style={{ marginTop: 8, fontSize: 12 }}>
                    Rate more conversations to see performance trends.
                  </p>
                </EmptyState>
              </ChartContainer>
            )}
          </AnalyticsSection>
        )}
        
        {/* Training Priorities */}
        {trainingPriorities.length > 0 && (
          <ConversationsToRate>
            <SectionTitle>
              {Icons.brain} What Should I Train Next?
            </SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {trainingPriorities.slice(0, 5).map((p, idx) => (
                <div key={idx} style={{
                  background: 'rgba(255,255,255,0.04)',
                  borderRadius: 10,
                  padding: '12px 16px',
                  border: '1px solid rgba(255,255,255,0.06)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{p.category}</span>
                    <span style={{
                      background: p.gapScore >= 3 ? 'rgba(239,68,68,0.2)' : p.gapScore >= 2 ? 'rgba(251,191,36,0.2)' : 'rgba(34,197,94,0.2)',
                      color: p.gapScore >= 3 ? '#fca5a5' : p.gapScore >= 2 ? '#fcd34d' : '#86efac',
                      padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 500
                    }}>
                      {p.gapScore} gap{p.gapScore > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {p.actions.map((action, ai) => (
                      <span key={ai} onClick={() => {
                        if (action.type === 'flow') navigate('/synthora-hub?tab=flows');
                        else if (action.type === 'teaching') navigate('/synthora-hub?tab=training');
                        else if (action.type === 'strategy') navigate('/synthora-hub?tab=strategies');
                        else if (action.type === 'rule') navigate('/synthora-hub?tab=rules');
                      }} style={{
                        background: 'rgba(99,102,241,0.15)',
                        color: '#a5b4fc',
                        padding: '4px 10px',
                        borderRadius: 8,
                        fontSize: 12,
                        cursor: 'pointer',
                        transition: 'background 0.2s'
                      }}>
                        {action.label}
                      </span>
                    ))}
                  </div>
                  {p.negativeRate > 0 && (
                    <div style={{ marginTop: 6, fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                      {p.negativeRate}% negative rating ({p.ratingStats?.total} rated)
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ConversationsToRate>
        )}

        {/* Pattern-Detected Suggestions */}
        {patternSuggestions.length > 0 && (
          <ConversationsToRate>
            <SectionTitle>
              {Icons.lightbulb || '💡'} Pattern-Detected Suggestions
            </SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {patternSuggestions.map((s) => (
                <div key={s._id} style={{
                  background: 'rgba(251,191,36,0.06)',
                  borderRadius: 10,
                  padding: '14px 16px',
                  border: '1px solid rgba(251,191,36,0.18)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ color: '#fcd34d', fontWeight: 600, fontSize: 14 }}>{s.category}</span>
                    <span style={{
                      background: 'rgba(251,191,36,0.15)',
                      color: '#fde68a',
                      padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 500
                    }}>
                      {s.conversationCount} negative case{s.conversationCount > 1 ? 's' : ''}
                    </span>
                  </div>
                  <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, margin: '0 0 10px 0', lineHeight: 1.4 }}>
                    {s.description}
                  </p>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => createTeachingFromPattern(s._id)} style={{
                      background: 'rgba(99,102,241,0.2)',
                      color: '#a5b4fc',
                      border: '1px solid rgba(99,102,241,0.3)',
                      borderRadius: 8,
                      padding: '6px 14px',
                      fontSize: 12,
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}>
                      Create Teaching
                    </button>
                    <button onClick={() => dismissPattern(s._id)} style={{
                      background: 'transparent',
                      color: 'rgba(255,255,255,0.4)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 8,
                      padding: '6px 14px',
                      fontSize: 12,
                      cursor: 'pointer'
                    }}>
                      Dismiss
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </ConversationsToRate>
        )}

        {/* Conversations to Rate */}
        {conversationsToRate.length > 0 && (
          <ConversationsToRate>
            <SectionTitle>
              {Icons.star} Rate Recent Conversations
            </SectionTitle>
            <ListCount>
              Showing {Math.min(convoRateLimit, conversationsToRate.length)} of {conversationsToRate.length} most recent
              {totalToRate > conversationsToRate.length && ` · ${totalToRate} unrated in total`}
            </ListCount>
            {conversationsToRate.slice(0, convoRateLimit).map(conv => (
              <RateCard key={conv.id}>
                <div style={{ flex: 1 }}>
                  <RateCardInfo>
                    <RateCardCategory>{conv.userName || 'Anonymous'}</RateCardCategory>
                    <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
                      {conv.messageCount} messages
                    </span>
                    <RateCardTime>
                      {new Date(conv.startTime).toLocaleDateString()}
                    </RateCardTime>
                    <ViewConvoButton onClick={() => navigate(`/workspace/${conv.id || conv.threadId}`)}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      View
                    </ViewConvoButton>
                    {(conv.aiReview || aiReviewResults[conv.id]) ? (
                      <GradeBadge
                        $score={(aiReviewResults[conv.id] || conv.aiReview)?.score || 0}
                        onClick={() => setSelectedReview(aiReviewResults[conv.id] || conv.aiReview)}
                      >
                        {(aiReviewResults[conv.id] || conv.aiReview)?.score}/100
                      </GradeBadge>
                    ) : (
                      <AIReviewButton
                        onClick={() => triggerAIReview(conv.id)}
                        disabled={aiReviewLoading[conv.id]}
                      >
                        {aiReviewLoading[conv.id] ? (
                          <><Spinner /> Reviewing...</>
                        ) : (
                          <>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2a4 4 0 0 1 4 4c0 1.95-1.4 3.58-3.25 3.93L12 22l-.75-12.07A4.001 4.001 0 0 1 12 2z"/><path d="M9 6.5L2 12l7 5.5"/><path d="M15 6.5L22 12l-7 5.5"/></svg>
                            AI Review
                          </>
                        )}
                      </AIReviewButton>
                    )}
                  </RateCardInfo>
                  {conv.preview && <ConvPreview>"{conv.preview}"</ConvPreview>}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                    <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>Outcome:</span>
                    <select
                      value={outcomeSelections[conv.id] || ''}
                      onChange={(e) => setOutcomeSelections(prev => ({...prev, [conv.id]: e.target.value || null}))}
                      style={{
                        background: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.15)',
                        borderRadius: 6,
                        color: '#fff',
                        padding: '4px 8px',
                        fontSize: 12,
                        cursor: 'pointer'
                      }}
                    >
                      <option value="" style={{ background: '#1a1a2e', color: '#fff' }}>Not set</option>
                      <option value="approved" style={{ background: '#1a1a2e', color: '#fff' }}>Approved</option>
                      <option value="refused" style={{ background: '#1a1a2e', color: '#fff' }}>Refused</option>
                      <option value="abandoned" style={{ background: '#1a1a2e', color: '#fff' }}>Abandoned</option>
                      <option value="referred" style={{ background: '#1a1a2e', color: '#fff' }}>Referred</option>
                      <option value="pending" style={{ background: '#1a1a2e', color: '#fff' }}>Pending</option>
                    </select>
                    <button
                      onClick={() => setConversationToRemove(conv)}
                      style={{
                        background: 'transparent',
                        color: 'rgba(255,255,255,0.5)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: 6,
                        padding: '4px 10px',
                        fontSize: 12,
                        cursor: 'pointer'
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
                <RatingButtons>
                  <RatingButton $rating="excellent" onClick={() => rateConversation(conv.id, 'excellent')}>
                    Excellent
                  </RatingButton>
                  <RatingButton $rating="good" onClick={() => rateConversation(conv.id, 'good')}>
                    Good
                  </RatingButton>
                  <RatingButton $rating="needs_improvement" onClick={() => rateConversation(conv.id, 'needs_improvement')}>
                    Needs Work
                  </RatingButton>
                  <RatingButton $rating="poor" onClick={() => rateConversation(conv.id, 'poor')}>
                    Poor
                  </RatingButton>
                </RatingButtons>
              </RateCard>
            ))}
            {conversationsToRate.length > convoRateLimit && (
              <ShowMoreButton onClick={() => setConvoRateLimit(l => l + 10)}>
                Show More ({conversationsToRate.length - convoRateLimit} remaining)
              </ShowMoreButton>
            )}
            {convoRateLimit > 5 && (
              <ShowMoreButton onClick={() => setConvoRateLimit(5)} style={{ marginTop: 4 }}>
                Show Less
              </ShowMoreButton>
            )}
          </ConversationsToRate>
        )}
        
        {/* AI Suggestions */}
        {dashboard?.suggestions?.topSuggestions?.length > 0 && (
          <SuggestionsSection>
            <SectionTitle>
              {Icons.lightbulb} AI-Powered Suggestions
            </SectionTitle>
            {dashboard.suggestions.topSuggestions.map((suggestion, idx) => (
              <SuggestionCard key={suggestion.id} $delay={`${0.3 + idx * 0.1}s`}>
                <SuggestionHeader>
                  <SuggestionTitle>
                    <PriorityBadge $priority={suggestion.priority}>
                      {suggestion.priority}
                    </PriorityBadge>
                    <h4>{suggestion.title}</h4>
                    <TypeBadge>{suggestion.type?.replace('_', ' ')}</TypeBadge>
                  </SuggestionTitle>
                </SuggestionHeader>
                <SuggestionDescription>
                  {suggestion.description}
                </SuggestionDescription>
                {suggestion.evidence?.sampleFeedback?.length > 0 && (
                  <SuggestionDescription style={{ fontStyle: 'italic', borderLeft: '2px solid rgba(99, 102, 241, 0.3)', paddingLeft: 12 }}>
                    Expert feedback: "{suggestion.evidence.sampleFeedback[0]}"
                  </SuggestionDescription>
                )}
                <SuggestionActions>
                  {suggestion.oneClickApply && (
                    <ApplyButton 
                      onClick={() => applySuggestion(suggestion)}
                      disabled={applyingId === suggestion.id}
                    >
                      {applyingId === suggestion.id ? (
                        <LoadingSpinner>{Icons.loader}</LoadingSpinner>
                      ) : Icons.apply}
                      {applyingId === suggestion.id ? 'Applying...' : 'Apply Now'}
                    </ApplyButton>
                  )}
                  <DismissButton onClick={() => setSelectedSuggestion(suggestion)}>View Details</DismissButton>
                </SuggestionActions>
              </SuggestionCard>
            ))}
          </SuggestionsSection>
        )}
        
        {/* Empty state for suggestions */}
        {(!dashboard?.suggestions?.topSuggestions || dashboard.suggestions.topSuggestions.length === 0) && (
          <EmptyState>
            {Icons.check}
            <p>No suggestions at this time. Your brain is performing well!</p>
            <p style={{ marginTop: 8, fontSize: 12 }}>
              Rate more conversations to help the system learn and generate improvement suggestions.
            </p>
          </EmptyState>
        )}

        {/* ===== Section 1: Auto-Learning Activity Feed ===== */}
        <NewSection $delay="0.3s">
          <SectionTitle>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2a7 7 0 0 1 7 7c0 2.38-1.19 4.47-3 5.74V17a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 0 1 7-7z"/><path d="M10 21h4"/></svg>
            Auto-Learning Activity
            {!hasRealAutoLearnData && <DemoBadge>Demo Data</DemoBadge>}
          </SectionTitle>
          <NewSectionCard>
            {(() => {
              const srcTeachings = hasRealAutoLearnData ? teachings : displayTeachings;
              const autoLearned = Array.isArray(srcTeachings) ? srcTeachings.filter(t => t.source?.type === 'system_learned' || t.tags?.includes?.('auto-learned')) : [];
              const shadowTeachings = autoLearned.filter(t => t.status === 'shadow');
              const promoted = autoLearned.filter(t => t.status === 'active' && t.source?.type === 'system_learned');
              const demoted = autoLearned.filter(t => t.status === 'inactive' && t.source?.type === 'system_learned');
              
              return (
                <>
                  <StatsRow>
                    <MiniStat>
                      <MiniStatValue $color="#a5b4fc">{autoLearned.length}</MiniStatValue>
                      <MiniStatLabel>Total Auto-Learned</MiniStatLabel>
                    </MiniStat>
                    <MiniStat>
                      <MiniStatValue $color="#f59e0b">{shadowTeachings.length}</MiniStatValue>
                      <MiniStatLabel>In Shadow</MiniStatLabel>
                    </MiniStat>
                    <MiniStat>
                      <MiniStatValue $color="#10b981">{promoted.length}</MiniStatValue>
                      <MiniStatLabel>Promoted</MiniStatLabel>
                    </MiniStat>
                    <MiniStat>
                      <MiniStatValue $color="#ef4444">{demoted.length}</MiniStatValue>
                      <MiniStatLabel>Demoted</MiniStatLabel>
                    </MiniStat>
                  </StatsRow>
                  <TimelineList>
                    {autoLearned.slice(0, autoLearnLimit).map((t, idx) => {
                      const isReinforcement = t.source?.sentiment === 'positive' || t.source?.fromRating === 'excellent';
                      const borderColor = isReinforcement ? '#10b981' : '#ef4444';
                      let badgeBg, badgeColor, badgeText;
                      if (t.status === 'shadow') { badgeBg = 'rgba(245, 158, 11, 0.2)'; badgeColor = '#f59e0b'; badgeText = 'Shadow Testing'; }
                      else if (t.status === 'active' && t.source?.type === 'system_learned') { badgeBg = 'rgba(16, 185, 129, 0.2)'; badgeColor = '#10b981'; badgeText = 'Auto-Promoted'; }
                      else if (t.status === 'inactive') { badgeBg = 'rgba(239, 68, 68, 0.2)'; badgeColor = '#ef4444'; badgeText = 'Auto-Demoted'; }
                      else { badgeBg = 'rgba(99, 102, 241, 0.2)'; badgeColor = '#a5b4fc'; badgeText = t.status || 'Active'; }
                      
                      return (
                        <TimelineItem key={t._id || idx} $borderColor={borderColor}>
                          <TimelineItemHeader>
                            <TimelineItemTitle>{t.name || 'Untitled Teaching'}</TimelineItemTitle>
                            <StatusBadge $bg={badgeBg} $color={badgeColor}>{badgeText}</StatusBadge>
                          </TimelineItemHeader>
                          <TimelineItemMeta>
                            {isReinforcement ? 'From excellent conversation' : 'From poor conversation'} · {t.createdAt ? new Date(t.createdAt).toLocaleDateString() : 'Unknown date'}
                            {t.shadowStats && ` · ${t.shadowStats.conversationsTested || 0} tested, ${t.shadowStats.successRate || 0}% success`}
                          </TimelineItemMeta>
                        </TimelineItem>
                      );
                    })}
                  </TimelineList>
                  {autoLearned.length > autoLearnLimit && (
                    <ShowMoreButton onClick={() => setAutoLearnLimit(l => l + 10)}>
                      Show More ({autoLearned.length - autoLearnLimit} remaining)
                    </ShowMoreButton>
                  )}
                  {autoLearnLimit > 5 && (
                    <ShowMoreButton onClick={() => setAutoLearnLimit(5)} style={{ marginTop: 4 }}>
                      Show Less
                    </ShowMoreButton>
                  )}
                </>
              );
            })()}
          </NewSectionCard>
        </NewSection>

        {/* ===== Section 2: Teaching Validation Pipeline ===== */}
        <NewSection $delay="0.35s">
          <SectionTitle>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
            Teaching Validation Pipeline
            {!hasRealAutoLearnData && <DemoBadge>Demo Data</DemoBadge>}
          </SectionTitle>
          <NewSectionCard>
            {(() => {
              const srcTeachings = hasRealAutoLearnData ? teachings : displayTeachings;
              const shadowTeachings = Array.isArray(srcTeachings) ? srcTeachings.filter(t => t.status === 'shadow') : [];
              
              if (shadowTeachings.length === 0) {
                return (
                  <EmptyState style={{ padding: '32px' }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.4, marginBottom: 12 }}>
                      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                    </svg>
                    <p>No teachings in validation pipeline.</p>
                  </EmptyState>
                );
              }
              
              return (
                <>
                <PipelineRow>
                  {shadowTeachings.slice(0, pipelineLimit).map((t, idx) => {
                    const tested = t.shadowStats?.conversationsTested || t.validationPipeline?.conversationsTested || 0;
                    const threshold = t.shadowStats?.promotionThreshold || t.validationPipeline?.promotionThreshold || 5;
                    const successRate = t.shadowStats?.successRate || t.validationPipeline?.successRate || 0;
                    const successStreak = t.shadowStats?.successStreak || t.validationPipeline?.successStreak || 0;
                    const failStreak = t.shadowStats?.failStreak || t.validationPipeline?.failStreak || 0;
                    const percent = Math.min(100, (tested / threshold) * 100);
                    const createdAt = t.createdAt ? new Date(t.createdAt) : new Date();
                    const daysInShadow = Math.max(0, Math.round((Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24)));
                    
                    let borderColor;
                    if (percent >= 80) borderColor = 'rgba(16, 185, 129, 0.5)';
                    else if (failStreak >= 2) borderColor = 'rgba(239, 68, 68, 0.5)';
                    else borderColor = 'rgba(245, 158, 11, 0.3)';
                    
                    return (
                      <PipelineCard key={t._id || idx} $borderColor={borderColor}>
                        <TimelineItemTitle style={{ marginBottom: 4 }}>{t.name || 'Untitled'}</TimelineItemTitle>
                        <TimelineItemMeta style={{ marginBottom: 10 }}>
                          {t.triggers?.[0] && `Trigger: "${t.triggers[0]}"`}
                          {!t.triggers?.[0] && t.keyword && `Keyword: "${t.keyword}"`}
                        </TimelineItemMeta>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>{tested}/{threshold} conversations tested</div>
                        <ProgressBarContainer>
                          <ProgressBarFill $percent={percent} $color={percent >= 80 ? '#10b981' : percent >= 40 ? '#f59e0b' : '#6366f1'} />
                        </ProgressBarContainer>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                          <span style={{ color: '#10b981', fontWeight: 600 }}>{successRate}% success</span>
                          <span style={{ color: 'rgba(255,255,255,0.4)' }}>{daysInShadow}d in shadow</span>
                        </div>
                        {(successStreak > 0 || failStreak > 0) && (
                          <StreakDots>
                            {Array.from({ length: successStreak }).map((_, i) => <StreakDot key={'s'+i} $success />)}
                            {Array.from({ length: failStreak }).map((_, i) => <StreakDot key={'f'+i} />)}
                          </StreakDots>
                        )}
                      </PipelineCard>
                    );
                  })}
                </PipelineRow>
                {shadowTeachings.length > pipelineLimit && (
                  <ShowMoreButton onClick={() => setPipelineLimit(l => l + 10)}>
                    Show More ({shadowTeachings.length - pipelineLimit} remaining)
                  </ShowMoreButton>
                )}
                {pipelineLimit > 5 && (
                  <ShowMoreButton onClick={() => setPipelineLimit(5)} style={{ marginTop: 4 }}>
                    Show Less
                  </ShowMoreButton>
                )}
                </>
              );
            })()}
          </NewSectionCard>
        </NewSection>

        {/* ===== Section 3: A/B Testing Panel ===== */}
        <NewSection $delay="0.4s">
          <SectionTitle>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="18" rx="2"/><line x1="12" y1="3" x2="12" y2="21"/></svg>
            A/B Testing
            {!hasRealAbTestData && <DemoBadge>Demo Data</DemoBadge>}
          </SectionTitle>
          <NewSectionCard>
            {(() => {
              const activeTest = hasRealAbTestData ? abTestData : displayAbTest;
              return activeTest && activeTest.isActive ? true : false;
            })() ? (() => {
              const activeTest = hasRealAbTestData ? abTestData : displayAbTest;
              const a = activeTest.versionAStats || {};
              const b = activeTest.versionBStats || {};
              const aTotal = (a.positiveRatings || 0) + (a.negativeRatings || 0);
              const bTotal = (b.positiveRatings || 0) + (b.negativeRatings || 0);
              const aRate = aTotal > 0 ? Math.round((a.positiveRatings / aTotal) * 100) : 0;
              const bRate = bTotal > 0 ? Math.round((b.positiveRatings / bTotal) * 100) : 0;
              const aWinning = aRate > bRate && aTotal >= 3;
              const bWinning = bRate > aRate && bTotal >= 3;
              const totalData = aTotal + bTotal;
              const startedAt = activeTest.startedAt ? new Date(activeTest.startedAt) : null;
              const daysRunning = startedAt ? Math.max(0, Math.round((Date.now() - startedAt.getTime()) / (1000 * 60 * 60 * 24))) : 0;
              
              let significanceText = 'Need more data';
              let significanceColor = 'rgba(255,255,255,0.5)';
              if (totalData >= 10) {
                if (aWinning) { significanceText = `Version A winning (+${aRate - bRate}%)`; significanceColor = '#10b981'; }
                else if (bWinning) { significanceText = `Version B winning (+${bRate - aRate}%)`; significanceColor = '#10b981'; }
                else { significanceText = 'Too close to call'; significanceColor = '#f59e0b'; }
              }
              
              return (
                <>
                  <ABSplitContainer>
                    <ABSide $side="left" $winning={aWinning}>
                      <ABVersionLabel>Version {activeTest.versionA}</ABVersionLabel>
                      <CircularProgress $size={70} $value={aRate} $color={aWinning ? '#10b981' : '#6366f1'}>
                        <CircularProgressValue>{aRate}%</CircularProgressValue>
                      </CircularProgress>
                      <ABMetricLabel style={{ marginTop: 12 }}>Success Rate</ABMetricLabel>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 8 }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 18, fontWeight: 600, color: '#fff' }}>{a.conversations || 0}</div>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Conversations</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 18, fontWeight: 600, color: '#10b981' }}>{a.positiveRatings || 0}</div>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Positive</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 18, fontWeight: 600, color: '#ef4444' }}>{a.negativeRatings || 0}</div>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Negative</div>
                        </div>
                      </div>
                    </ABSide>
                    <ABDivider>
                      <ABVsBadge>VS</ABVsBadge>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 8 }}>{activeTest.trafficSplit || 50}/{100 - (activeTest.trafficSplit || 50)}</div>
                    </ABDivider>
                    <ABSide $side="right" $winning={bWinning}>
                      <ABVersionLabel>Version {activeTest.versionB}</ABVersionLabel>
                      <CircularProgress $size={70} $value={bRate} $color={bWinning ? '#10b981' : '#6366f1'}>
                        <CircularProgressValue>{bRate}%</CircularProgressValue>
                      </CircularProgress>
                      <ABMetricLabel style={{ marginTop: 12 }}>Success Rate</ABMetricLabel>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 8 }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 18, fontWeight: 600, color: '#fff' }}>{b.conversations || 0}</div>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Conversations</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 18, fontWeight: 600, color: '#10b981' }}>{b.positiveRatings || 0}</div>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Positive</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 18, fontWeight: 600, color: '#ef4444' }}>{b.negativeRatings || 0}</div>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Negative</div>
                        </div>
                      </div>
                    </ABSide>
                  </ABSplitContainer>
                  <ABFooter>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: significanceColor }}>{significanceText}</div>
                      {startedAt && <DurationBadge>Running for {daysRunning} day{daysRunning !== 1 ? 's' : ''}</DurationBadge>}
                    </div>
                    <DangerButton onClick={stopAbTest} disabled={stoppingAbTest || !hasRealAbTestData}>
                      {stoppingAbTest ? Icons.loader : '■'} Stop Test
                    </DangerButton>
                  </ABFooter>
                </>
              );
            })() : (
              <div>
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, marginBottom: 20, marginTop: 0 }}>
                  Compare two brain versions side-by-side to see which performs better with real conversations.
                </p>
                <ABFormRow>
                  <ABFormField>
                    <label>Version A</label>
                    <input
                      type="number"
                      min="1"
                      placeholder="e.g. 1"
                      value={abTestForm.versionA}
                      onChange={e => setAbTestForm(f => ({ ...f, versionA: e.target.value }))}
                    />
                  </ABFormField>
                  <ABFormField>
                    <label>Version B</label>
                    <input
                      type="number"
                      min="1"
                      placeholder="e.g. 2"
                      value={abTestForm.versionB}
                      onChange={e => setAbTestForm(f => ({ ...f, versionB: e.target.value }))}
                    />
                  </ABFormField>
                  <ABFormField>
                    <label>Traffic Split (A%)</label>
                    <input
                      type="range"
                      min="10"
                      max="90"
                      step="10"
                      value={abTestForm.trafficSplit}
                      onChange={e => setAbTestForm(f => ({ ...f, trafficSplit: Number(e.target.value) }))}
                      style={{ width: 120 }}
                    />
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>
                      {abTestForm.trafficSplit}/{100 - abTestForm.trafficSplit}
                    </div>
                  </ABFormField>
                  <ApplyButton
                    onClick={startAbTest}
                    disabled={startingAbTest || !abTestForm.versionA || !abTestForm.versionB}
                    style={{ height: 38 }}
                  >
                    {startingAbTest ? <><LoadingSpinner>{Icons.loader}</LoadingSpinner> Starting...</> : 'Start A/B Test'}
                  </ApplyButton>
                </ABFormRow>
              </div>
            )}
          </NewSectionCard>
        </NewSection>

        {/* ===== Section 4: Strategy Effectiveness Dashboard ===== */}
        <NewSection $delay="0.45s">
          <SectionTitle>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
            Strategy Effectiveness
            {!hasRealStrategyData && <DemoBadge>Demo Data</DemoBadge>}
          </SectionTitle>
          <NewSectionCard>
            {(() => {
              const strats = Array.isArray(displayStrategies) ? displayStrategies : [];
              
              if (strats.length === 0) {
                return (
                  <EmptyState style={{ padding: '32px' }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.4, marginBottom: 12 }}>
                      <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>
                    </svg>
                    <p>No expert strategies configured yet.</p>
                  </EmptyState>
                );
              }
              
              const sorted = [...strats].sort((a, b) => (b.triggeredCount || 0) - (a.triggeredCount || 0));
              const avgSuccess = sorted.length > 0
                ? Math.round(sorted.reduce((sum, s) => sum + (s.successRate || 0), 0) / sorted.length)
                : 0;
              const topPerformer = sorted.find(s => (s.triggeredCount || 0) > 0);
              
              return (
                <>
                  <StatsRow>
                    <MiniStat>
                      <MiniStatValue $color="#a5b4fc">{sorted.length}</MiniStatValue>
                      <MiniStatLabel>Total Strategies</MiniStatLabel>
                    </MiniStat>
                    <MiniStat>
                      <MiniStatValue $color="#10b981">{avgSuccess}%</MiniStatValue>
                      <MiniStatLabel>Avg Success Rate</MiniStatLabel>
                    </MiniStat>
                    <MiniStat>
                      <MiniStatValue $color="#f59e0b">{topPerformer?.name?.slice(0, 12) || '—'}</MiniStatValue>
                      <MiniStatLabel>Top Performer</MiniStatLabel>
                    </MiniStat>
                  </StatsRow>
                  <StrategyGrid>
                    {sorted.slice(0, strategyLimit).map((s, idx) => {
                      const rate = s.successRate || 0;
                      const triggered = s.triggeredCount || 0;
                      let statusText, statusColor;
                      if (triggered === 0) { statusText = 'Not Tested'; statusColor = 'rgba(255,255,255,0.3)'; }
                      else if (rate > 70) { statusText = 'High Performer'; statusColor = '#10b981'; }
                      else if (rate >= 30) { statusText = 'Needs Review'; statusColor = '#f59e0b'; }
                      else { statusText = 'Underperforming'; statusColor = '#ef4444'; }
                      
                      return (
                        <StrategyCard key={s._id || idx}>
                          <StrategyName>{s.name || 'Unnamed Strategy'}</StrategyName>
                          <StrategyMeta>
                            <span>Triggered {triggered}x</span>
                            <StatusBadge $bg={`${statusColor}22`} $color={statusColor}>{statusText}</StatusBadge>
                          </StrategyMeta>
                          <ProgressBarContainer>
                            <ProgressBarFill $percent={rate} $color={statusColor} />
                          </ProgressBarContainer>
                          <div style={{ fontSize: 13, fontWeight: 600, color: statusColor, textAlign: 'right' }}>{rate}%</div>
                        </StrategyCard>
                      );
                    })}
                  </StrategyGrid>
                  {sorted.length > strategyLimit && (
                    <ShowMoreButton onClick={() => setStrategyLimit(l => l + 10)}>
                      Show More ({sorted.length - strategyLimit} remaining)
                    </ShowMoreButton>
                  )}
                  {strategyLimit > 6 && (
                    <ShowMoreButton onClick={() => setStrategyLimit(6)} style={{ marginTop: 4 }}>
                      Show Less
                    </ShowMoreButton>
                  )}
                </>
              );
            })()}
          </NewSectionCard>
        </NewSection>

        {/* ===== Section 5: Pathway Funnel Analytics ===== */}
        <NewSection $delay="0.5s">
          <SectionTitle>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
            Pathway Conversion Funnel
            {!hasRealFunnelData && <DemoBadge>Demo Data</DemoBadge>}
          </SectionTitle>
          <NewSectionCard>
            {(() => {
              const fd = displayFunnel;
              if (!fd || !fd.funnel || fd.funnel.length === 0) {
                return (
                  <EmptyState style={{ padding: '32px' }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.4, marginBottom: 12 }}>
                      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                    </svg>
                    <p>No pathway data yet. Pathway tracking requires opportunity maps.</p>
                  </EmptyState>
                );
              }
              
              const overview = fd.overview || {};
              const maxDetected = Math.max(...fd.funnel.map(f => f.detected), 1);
              
              return (
                <>
                  <StatsRow>
                    <MiniStat>
                      <MiniStatValue $color="#a5b4fc">{overview.totalConversations || 0}</MiniStatValue>
                      <MiniStatLabel>Total Conversations</MiniStatLabel>
                    </MiniStat>
                    <MiniStat>
                      <MiniStatValue $color="#6366f1">{overview.pathwayDetectionRate || 0}%</MiniStatValue>
                      <MiniStatLabel>Detection Rate</MiniStatLabel>
                    </MiniStat>
                    <MiniStat>
                      <MiniStatValue $color="#818cf8">{overview.uniquePathways || 0}</MiniStatValue>
                      <MiniStatLabel>Unique Pathways</MiniStatLabel>
                    </MiniStat>
                    <MiniStat>
                      <MiniStatValue $color="#10b981">
                        {fd.funnel.length > 0 ? Math.round(fd.funnel.reduce((s, f) => s + (f.conversionRate || 0), 0) / fd.funnel.length) : 0}%
                      </MiniStatValue>
                      <MiniStatLabel>Avg Conversion</MiniStatLabel>
                    </MiniStat>
                  </StatsRow>
                  <FunnelContainer>
                    {fd.funnel.slice(0, funnelLimit).map((f, idx) => (
                      <FunnelRow key={f.pathwayId || idx}>
                        <FunnelPathwayName>
                          {f.pathwayName || f.pathwayId}
                          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginLeft: 12 }}>
                            {f.conversionRate}% conversion · {Math.round(f.avgMessages || 0)} avg msgs
                          </span>
                        </FunnelPathwayName>
                        <FunnelBars>
                          <FunnelBarRow>
                            <FunnelBarLabel>Detected</FunnelBarLabel>
                            <FunnelBarTrack>
                              <FunnelBarFill $color="#6366f1" $percent={(f.detected / maxDetected) * 100}>{f.detected}</FunnelBarFill>
                            </FunnelBarTrack>
                            <FunnelBarValue>{f.detected}</FunnelBarValue>
                          </FunnelBarRow>
                          <FunnelBarRow>
                            <FunnelBarLabel>Engaged</FunnelBarLabel>
                            <FunnelBarTrack>
                              <FunnelBarFill $color="#818cf8" $percent={f.detected > 0 ? (f.engaged / f.detected) * 100 : 0} />
                            </FunnelBarTrack>
                            <FunnelBarValue>{f.engaged}</FunnelBarValue>
                          </FunnelBarRow>
                          <FunnelBarRow>
                            <FunnelBarLabel>Converted</FunnelBarLabel>
                            <FunnelBarTrack>
                              <FunnelBarFill $color="#10b981" $percent={f.detected > 0 ? (f.converted / f.detected) * 100 : 0} />
                            </FunnelBarTrack>
                            <FunnelBarValue>{f.converted}</FunnelBarValue>
                          </FunnelBarRow>
                        </FunnelBars>
                      </FunnelRow>
                    ))}
                  </FunnelContainer>
                  {fd.funnel.length > funnelLimit && (
                    <ShowMoreButton onClick={() => setFunnelLimit(l => l + 10)}>
                      Show More ({fd.funnel.length - funnelLimit} remaining)
                    </ShowMoreButton>
                  )}
                  {funnelLimit > 4 && (
                    <ShowMoreButton onClick={() => setFunnelLimit(4)} style={{ marginTop: 4 }}>
                      Show Less
                    </ShowMoreButton>
                  )}
                </>
              );
            })()}
          </NewSectionCard>
        </NewSection>

        {/* ===== Section 6: Visitor Memory Insights ===== */}
        <NewSection $delay="0.55s">
          <SectionTitle>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            Visitor Memory
            {!hasRealVisitorData && <DemoBadge>Demo Data</DemoBadge>}
          </SectionTitle>
          <NewSectionCard>
            {(() => {
              const vi = displayVisitors;
              if (!vi || vi.totalVisitors === 0) {
                return (
                  <EmptyState style={{ padding: '32px' }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.4, marginBottom: 12 }}>
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                    </svg>
                    <p>No visitor data yet. Visitor memory builds as conversations happen.</p>
                  </EmptyState>
                );
              }
              
              return (
                <>
                  <StatsRow>
                    <MiniStat>
                      <MiniStatValue $color="#a5b4fc">{vi.totalVisitors}</MiniStatValue>
                      <MiniStatLabel>Total Visitors</MiniStatLabel>
                    </MiniStat>
                    <MiniStat>
                      <MiniStatValue $color="#10b981">{vi.returningVisitors}</MiniStatValue>
                      <MiniStatLabel>Returning</MiniStatLabel>
                    </MiniStat>
                    <MiniStat>
                      <MiniStatValue $color="#f59e0b">{vi.avgEngagementScore}</MiniStatValue>
                      <MiniStatLabel>Avg Engagement</MiniStatLabel>
                    </MiniStat>
                    {vi.topCategories?.length > 0 && (
                      <MiniStat>
                        <MiniStatValue $color="#818cf8" style={{ fontSize: 16 }}>
                          {vi.topCategories.slice(0, 2).map(c => c.category).join(', ')}
                        </MiniStatValue>
                        <MiniStatLabel>Top Categories</MiniStatLabel>
                      </MiniStat>
                    )}
                  </StatsRow>
                  {vi.recentReturning?.length > 0 && (
                    <>
                      <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Recent Returning Visitors
                      </div>
                      <VisitorList>
                        {vi.recentReturning.slice(0, visitorLimit).map((v, idx) => (
                          <VisitorRow key={v.visitorId || idx}>
                            <VisitorInfo>
                              <VisitorAvatar>
                                {(v.displayName || 'A').charAt(0).toUpperCase()}
                              </VisitorAvatar>
                              <VisitorDetails>
                                <VisitorName>{v.displayName || 'Anonymous'}</VisitorName>
                                <VisitorMeta>
                                  {v.totalConversations} conversations · Last seen {v.lastSeen ? new Date(v.lastSeen).toLocaleDateString() : '—'}
                                </VisitorMeta>
                                {v.topInterests?.length > 0 && (
                                  <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                                    {v.topInterests.map((interest, i) => (
                                      <span key={i} style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, background: 'rgba(99, 102, 241, 0.15)', color: '#a5b4fc' }}>
                                        {interest}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </VisitorDetails>
                            </VisitorInfo>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{v.engagementScore}</div>
                                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>Score</div>
                              </div>
                              <EngagementBar>
                                <EngagementFill $score={v.engagementScore} />
                              </EngagementBar>
                            </div>
                          </VisitorRow>
                        ))}
                      </VisitorList>
                      {vi.recentReturning.length > visitorLimit && (
                        <ShowMoreButton onClick={() => setVisitorLimit(l => l + 10)}>
                          Show More ({vi.recentReturning.length - visitorLimit} remaining)
                        </ShowMoreButton>
                      )}
                      {visitorLimit > 5 && (
                        <ShowMoreButton onClick={() => setVisitorLimit(5)} style={{ marginTop: 4 }}>
                          Show Less
                        </ShowMoreButton>
                      )}
                    </>
                  )}
                </>
              );
            })()}
          </NewSectionCard>
        </NewSection>
      </ContentArea>

      {/* Suggestion Details Modal */}
      {selectedSuggestion && (
        <ModalOverlay onClick={() => setSelectedSuggestion(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <PriorityBadge $priority={selectedSuggestion.priority}>
                  {selectedSuggestion.priority}
                </PriorityBadge>
                {selectedSuggestion.title}
              </ModalTitle>
              <ModalCloseButton onClick={() => setSelectedSuggestion(null)}>×</ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <DetailSection>
                <DetailLabel>Type</DetailLabel>
                <DetailValue>
                  <TypeBadge>{selectedSuggestion.type?.replace('_', ' ')}</TypeBadge>
                </DetailValue>
              </DetailSection>
              
              <DetailSection>
                <DetailLabel>Description</DetailLabel>
                <DetailValue>{selectedSuggestion.description}</DetailValue>
              </DetailSection>
              
              {selectedSuggestion.actionRequired && (
                <DetailSection>
                  <DetailLabel>Action Required</DetailLabel>
                  <DetailValue>{selectedSuggestion.actionRequired}</DetailValue>
                </DetailSection>
              )}
              
              {selectedSuggestion.evidence?.sampleFeedback?.length > 0 && (
                <DetailSection>
                  <DetailLabel>Expert Feedback ({selectedSuggestion.evidence.sampleFeedback.length} samples)</DetailLabel>
                  <EvidenceList>
                    {selectedSuggestion.evidence.sampleFeedback.map((feedback, idx) => (
                      <li key={idx}>"{feedback}"</li>
                    ))}
                  </EvidenceList>
                </DetailSection>
              )}
              
              {selectedSuggestion.evidence?.conversationPatterns && (
                <DetailSection>
                  <DetailLabel>Conversation Patterns</DetailLabel>
                  <DetailValue>{selectedSuggestion.evidence.conversationPatterns}</DetailValue>
                </DetailSection>
              )}
              
              {selectedSuggestion.expectedImpact && (
                <DetailSection>
                  <DetailLabel>Expected Impact</DetailLabel>
                  <DetailValue>{selectedSuggestion.expectedImpact}</DetailValue>
                </DetailSection>
              )}
            </ModalBody>
            <ModalFooter>
              <DismissButton onClick={() => setSelectedSuggestion(null)}>Close</DismissButton>
              {selectedSuggestion.oneClickApply && (
                <ApplyButton 
                  onClick={() => {
                    applySuggestion(selectedSuggestion);
                    setSelectedSuggestion(null);
                  }}
                >
                  {Icons.apply} Apply Now
                </ApplyButton>
              )}
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
      
      {/* Version Creation Confirmation Modal */}
      {versionModal === 'create' && (
        <ModalOverlay onClick={() => setVersionModal(null)}>
          <ModalContent onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <ModalHeader>
              <ModalTitle>Create Brain Version Checkpoint</ModalTitle>
              <ModalCloseButton onClick={() => setVersionModal(null)}>×</ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <VersionConfirmModal>
                <h4>What is a Brain Version?</h4>
                <p>
                  A brain version is a <strong>snapshot of your AI's knowledge</strong> at this moment in time. 
                  It captures all your flows, rules, strategies, and teachings.
                </p>
                
                <p><strong>Why create versions?</strong></p>
                <ul>
                  <li><strong>Track performance:</strong> Compare how different versions perform over time</li>
                  <li><strong>Safe experimentation:</strong> Make changes knowing you can rollback if needed</li>
                  <li><strong>Learning history:</strong> See how your AI has evolved and improved</li>
                </ul>
                
                <p style={{ color: '#f59e0b', marginTop: '16px' }}>
                  Note: Creating a version does NOT change your AI's behavior. It simply saves a checkpoint 
                  that you can later compare against or rollback to.
                </p>
              </VersionConfirmModal>
            </ModalBody>
            <VersionModalActions>
              <VersionModalButton onClick={() => setVersionModal(null)}>
                Cancel
              </VersionModalButton>
              <VersionModalButton $primary onClick={createNewVersion}>
                Create Version v{(dashboard?.brain?.currentVersion || 0) + 1}
              </VersionModalButton>
            </VersionModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {selectedReview && (
        <ReviewModalOverlay onClick={() => setSelectedReview(null)}>
          <ReviewModalContent onClick={e => e.stopPropagation()}>
            <ReviewModalTitle>
              <GradeBadge $score={selectedReview.score}>{selectedReview.score}/100</GradeBadge>
              AI Conversation Review
              <button onClick={() => setSelectedReview(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 18 }}>x</button>
            </ReviewModalTitle>

            {selectedReview.summary && (
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, marginBottom: 20, lineHeight: 1.5 }}>{selectedReview.summary}</p>
            )}

            <div style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 14, marginBottom: 10, color: 'rgba(255,255,255,0.8)' }}>Dimensions</h4>
              {[
                { label: 'Question Coverage', key: 'questionCoverage' },
                { label: 'Classification Timing', key: 'classificationTiming' },
                { label: 'Topic Depth', key: 'topicDepth' },
                { label: 'Admissibility Checks', key: 'admissibilityChecks' },
                { label: 'Brain Utilization', key: 'brainUtilization' }
              ].map(dim => {
                const val = selectedReview.dimensions?.[dim.key] || 0;
                return (
                  <DimensionRow key={dim.key}>
                    <span style={{ color: 'rgba(255,255,255,0.6)' }}>{dim.label}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <DimensionBar><DimensionFill $value={val} style={{ width: `${val}%` }} /></DimensionBar>
                      <span style={{ fontSize: 12, fontWeight: 600, minWidth: 30, textAlign: 'right' }}>{val}</span>
                    </div>
                  </DimensionRow>
                );
              })}
            </div>

            {selectedReview.issues?.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <h4 style={{ fontSize: 14, marginBottom: 10, color: 'rgba(255,255,255,0.8)' }}>Issues Found ({selectedReview.issues.length})</h4>
                {selectedReview.issues.map((issue, idx) => (
                  <div key={idx} style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.06)', fontSize: 13 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <IssueBadge $severity={issue.severity}>{issue.severity}</IssueBadge>
                      <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>{issue.type?.replace(/_/g, ' ')}</span>
                    </div>
                    <span style={{ color: 'rgba(255,255,255,0.7)' }}>{issue.description}</span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 16, fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
              <span>Topic: <strong style={{ color: '#c084fc' }}>{selectedReview.topicDetected}</strong></span>
              <span>Teachings created: <strong style={{ color: '#10b981' }}>{selectedReview.teachingsCreated || 0}</strong></span>
              <span>Model: <strong>{selectedReview.modelUsed}</strong></span>
            </div>
          </ReviewModalContent>
        </ReviewModalOverlay>
      )}

      {conversationToRemove && (
        <ModalOverlay onClick={() => setConversationToRemove(null)}>
          <ModalContent onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <ModalHeader>
              <ModalTitle>Remove Conversation From Rating</ModalTitle>
              <ModalCloseButton onClick={() => setConversationToRemove(null)}>×</ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <VersionConfirmModal>
                <h4>Are you sure?</h4>
                <p>
                  This will remove <strong>{conversationToRemove.userName || 'this conversation'}</strong> from the
                  Brain Learning rating queue.
                </p>
                <p>
                  Use this for spam, testing, or conversations that should not affect the brain score.
                </p>
              </VersionConfirmModal>
            </ModalBody>
            <VersionModalActions>
              <VersionModalButton onClick={() => setConversationToRemove(null)}>
                Cancel
              </VersionModalButton>
              <VersionModalButton $primary onClick={() => removeConversationFromRating(conversationToRemove.id)}>
                Remove
              </VersionModalButton>
            </VersionModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </ModuleContainer>
  );
};

export default BrainLearningModule;

